import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LogOut,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Upload,
  Eye,
  Edit,
  User,
  Users,
  Plane,
  Settings,
  DollarSign,
  UserPlus,
  Trash2,
  Key,
  Plus,
  Save,
  Wallet,
  MinusCircle,
  Globe,
  RefreshCw,
  RefreshCcw,
  Play,
  Square,
  AlertCircle,
  Download,
  FileImage,
  File,
  Zap,
  Check,
  Ban
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import NinthwareTab from './NinthwareTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { admin, token, logout, isAuthenticated } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [statusUpdateOpen, setStatusUpdateOpen] = useState(false);
  const [uploadVisaOpen, setUploadVisaOpen] = useState(false);
  const [uploadTarget, setUploadTarget] = useState({ type: 'main', index: null });

  const [newStatus, setNewStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [visaFile, setVisaFile] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Super Admin State
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('applications');
  const [adminUsers, setAdminUsers] = useState([]);
  const [nationalityRates, setNationalityRates] = useState({});
  const [createAdminOpen, setCreateAdminOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [addNationalityOpen, setAddNationalityOpen] = useState(false);
  const [newAdminData, setNewAdminData] = useState({ email: '', password: '', name: '' });
  const [selectedAdminId, setSelectedAdminId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [newNationality, setNewNationality] = useState({ nationality: '', rate_30_days: '', rate_60_days: '' });
  const [editedRates, setEditedRates] = useState({});

  // Permissions/RBAC State
  const [myPermissions, setMyPermissions] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [selectedAdminForPermissions, setSelectedAdminForPermissions] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState([]);
  const [allAgentsForAssignment, setAllAgentsForAssignment] = useState([]);
  const [assignedAgentIds, setAssignedAgentIds] = useState([]);
  const [agentAssignModalOpen, setAgentAssignModalOpen] = useState(false);

  // Financial Statements State
  const [agentsStatements, setAgentsStatements] = useState([]);
  const [selectedAgentStatement, setSelectedAgentStatement] = useState(null);
  const [statementModalOpen, setStatementModalOpen] = useState(false);
  const [statementTransactions, setStatementTransactions] = useState([]);

  // B2B State
  const [b2bAgents, setB2BAgents] = useState([]);
  const [b2bApplications, setB2BApplications] = useState([]);
  const [b2bAppFilter, setB2bAppFilter] = useState('all'); // all, draft, submitted, pending, approved
  const [creditRequests, setCreditRequests] = useState([]);
  const [createAgentOpen, setCreateAgentOpen] = useState(false);
  const [addCreditOpen, setAddCreditOpen] = useState(false);
  const [editAgentRatesOpen, setEditAgentRatesOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [newAgentData, setNewAgentData] = useState({ 
    email: '', password: '', company_name: '', contact_person: '', phone: '' 
  });
  const [addCreditAmount, setAddCreditAmount] = useState('');
  const [addCreditNotes, setAddCreditNotes] = useState('');
  const [agentRates, setAgentRates] = useState({ '30_days': 400, '60_days': 600, '1m_extension': 300 });
  
  // Deduct Balance State
  const [deductBalanceOpen, setDeductBalanceOpen] = useState(false);
  const [deductAmount, setDeductAmount] = useState('');
  const [deductReason, setDeductReason] = useState('');
  
  // Admin Edit B2B Application State
  const [editB2BAppOpen, setEditB2BAppOpen] = useState(false);
  const [editingB2BApp, setEditingB2BApp] = useState(null);
  const [editB2BAppLoading, setEditB2BAppLoading] = useState(false);

  // Credit Limit State
  const [creditLimitOpen, setCreditLimitOpen] = useState(false);
  const [creditLimitAmount, setCreditLimitAmount] = useState('');

  // Send Back Application State
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [sendBackNote, setSendBackNote] = useState('');
  const [sendBackApp, setSendBackApp] = useState(null);

  // B2B Nationality Rates State
  const [b2bNationalityRates, setB2BNationalityRates] = useState([]);
  const [addB2BRateOpen, setAddB2BRateOpen] = useState(false);
  const [editB2BRateOpen, setEditB2BRateOpen] = useState(false);
  const [selectedB2BNationality, setSelectedB2BNationality] = useState(null);
  const [newB2BRate, setNewB2BRate] = useState({ 
    nationality: '', rate_30_days: '', rate_60_days: '', rate_1m_extension: '' 
  });

  // Document Requirements State (B2C)
  const [documentRequirements, setDocumentRequirements] = useState([]);
  const [addDocReqOpen, setAddDocReqOpen] = useState(false);
  const [docReqData, setDocReqData] = useState({
    nationalities: '',
    passport_photo: true,
    personal_photo: true,
    id_front: true,
    id_back: false
  });

  // B2B Document Requirements State
  const [b2bDocumentRequirements, setB2BDocumentRequirements] = useState([]);
  const [addB2BDocReqOpen, setAddB2BDocReqOpen] = useState(false);
  const [b2bDocReqData, setB2BDocReqData] = useState({
    nationalities: '',
    passport_doc: true,
    personal_photo_doc: true,
    id_front_doc: true,
    id_back_doc: true,
    frc_mrc_doc: false
  });

  // Agent-Specific Rates State
  const [selectedAgentForRates, setSelectedAgentForRates] = useState(null);
  const [agentRatesData, setAgentRatesData] = useState([]);
  const [addAgentRateOpen, setAddAgentRateOpen] = useState(false);
  const [editAgentRateOpen, setEditAgentRateOpen] = useState(false);
  const [selectedAgentRate, setSelectedAgentRate] = useState(null);
  const [newAgentRate, setNewAgentRate] = useState({
    nationality: '', rate_30_days: '', rate_60_days: '', rate_1m_extension: '',
    done_base_30_days: '', done_base_60_days: '', done_base_1m_extension: '',
    supporting_1_30_days: '', supporting_1_60_days: '', supporting_1_1m_extension: '',
    supporting_2_30_days: '', supporting_2_60_days: '', supporting_2_1m_extension: ''
  });

  // Nationality Groups State
  const [nationalityGroups, setNationalityGroups] = useState([]);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [editGroupOpen, setEditGroupOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [newGroupData, setNewGroupData] = useState({ name: '', nationalities: '' });
  const [applyGroupRatesOpen, setApplyGroupRatesOpen] = useState(false);
  const [groupRatesData, setGroupRatesData] = useState({
    group_id: '', rate_30_days: '', rate_60_days: '', rate_1m_extension: '',
    done_base_30_days: '', done_base_60_days: '', done_base_1m_extension: '',
    supporting_1_30_days: '', supporting_1_60_days: '', supporting_1_1m_extension: '',
    supporting_2_30_days: '', supporting_2_60_days: '', supporting_2_1m_extension: ''
  });

  // GDRFAD Integration State
  const [otpInput, setOtpInput] = useState('');
  const [gdrfadLoading, setGdrfadLoading] = useState(false);
  
  // GDRFAD Manual Mode State
  const [pendingGdrfadApps, setPendingGdrfadApps] = useState([]);
  const [gdrfadSyncHistory, setGdrfadSyncHistory] = useState([]);
  const [selectedGdrfadApp, setSelectedGdrfadApp] = useState(null);
  const [gdrfadUpdateStatus, setGdrfadUpdateStatus] = useState('approved');
  const [gdrfadUpdateRef, setGdrfadUpdateRef] = useState('');
  const [gdrfadUpdateNotes, setGdrfadUpdateNotes] = useState('');
  const [gdrfadUpdateModalOpen, setGdrfadUpdateModalOpen] = useState(false);
  
  // GDRFAD Manual Visa Upload State
  const [appsNeedingVisa, setAppsNeedingVisa] = useState([]);
  const [gdrfaManualVisaUploadOpen, setGdrfaManualVisaUploadOpen] = useState(false);
  const [gdrfaManualVisaApp, setGdrfaManualVisaApp] = useState(null);
  const [gdrfaVisaFile, setGdrfaVisaFile] = useState(null);
  const [gdrfaVisaUploading, setGdrfaVisaUploading] = useState(false);
  
  // GDRFAD Automation State
  const [autoSyncStatus, setAutoSyncStatus] = useState(null);
  const [autoSyncLoading, setAutoSyncLoading] = useState(false);
  const [autoSyncResults, setAutoSyncResults] = useState(null);
  const [gdrfadVisas, setGdrfadVisas] = useState([]);
  const [gdrfadSyncing, setGdrfadSyncing] = useState(false);
  const [singlePassportDownload, setSinglePassportDownload] = useState('');
  const [singleDownloadLoading, setSingleDownloadLoading] = useState(false);

  // B2B Application Details Modal State
  const [selectedB2BApp, setSelectedB2BApp] = useState(null);
  const [b2bAppDetailsOpen, setB2bAppDetailsOpen] = useState(false);

  // Receipt Modal State
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptImageUrl, setReceiptImageUrl] = useState('');

  // Visa Upload State
  const [visaUploadModalOpen, setVisaUploadModalOpen] = useState(false);
  const [visaUploadApp, setVisaUploadApp] = useState(null);
  const [visaUploading, setVisaUploading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    fetchApplications();
    checkSuperAdmin();
  }, [isAuthenticated]);

  const checkSuperAdmin = async () => {
    try {
      const response = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsSuperAdmin(response.data.is_super_admin);
      
      // Fetch my permissions
      await fetchMyPermissions();
      
      if (response.data.is_super_admin) {
        fetchAdminUsers();
        fetchNationalityRates();
        fetchB2BAgents();
        fetchB2BApplications();
        fetchCreditRequests();
        fetchB2BNationalityRates();
        fetchNationalityGroups();
        fetchDocumentRequirements();
        fetchB2BDocumentRequirements();
        fetchGdrfadPendingApps();
        fetchGdrfadSyncHistory();
        fetchAutoSyncStatusGlobal();
        fetchAppsNeedingVisa();
        fetchAgentsStatements();
        fetchAllAgentsForAssignment();
      } else {
        // Non-super admin - still fetch their assigned data
        fetchB2BAgents();
        fetchB2BApplications();
        fetchCreditRequests();
        fetchAgentsStatements(); // Fetch statements for assigned agents
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };

  const fetchMyPermissions = async () => {
    try {
      const response = await axios.get(`${API}/admin/permissions/my-permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyPermissions(response.data.permissions || []);
      setAvailablePermissions(response.data.available_permissions || {});
      setIsSuperAdmin(response.data.is_super_admin);
    } catch (error) {
      console.error('Error fetching my permissions:', error);
    }
  };

  const fetchAgentsStatements = async () => {
    try {
      const response = await axios.get(`${API}/admin/permissions/statements/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgentsStatements(response.data.agents || []);
    } catch (error) {
      console.error('Error fetching agents statements:', error);
    }
  };

  const fetchAgentStatement = async (agentId) => {
    try {
      const response = await axios.get(`${API}/admin/permissions/statements/agents/${agentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAgentStatement(response.data.agent);
      setStatementTransactions(response.data.transactions || []);
      setStatementModalOpen(true);
    } catch (error) {
      console.error('Error fetching agent statement:', error);
      toast.error('Failed to fetch statement');
    }
  };

  const fetchAllAgentsForAssignment = async () => {
    try {
      const response = await axios.get(`${API}/admin/permissions/all-agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllAgentsForAssignment(response.data.agents || []);
    } catch (error) {
      console.error('Error fetching agents for assignment:', error);
    }
  };

  const fetchAdminAssignedAgents = async (adminId) => {
    try {
      const response = await axios.get(`${API}/admin/permissions/admins/${adminId}/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const assignedIds = (response.data.assigned_agents || []).map(a => a.id);
      setAssignedAgentIds(assignedIds);
    } catch (error) {
      console.error('Error fetching assigned agents:', error);
      setAssignedAgentIds([]);
    }
  };

  const hasPermission = (permission) => {
    if (isSuperAdmin) return true;
    return myPermissions.includes(permission);
  };

  const fetchAutoSyncStatusGlobal = async () => {
    try {
      const response = await axios.get(`${API}/gdrfad/auto-sync/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutoSyncStatus(response.data);
    } catch (error) {
      console.error('Failed to get auto-sync status:', error);
    }
  };

  const fetchGdrfadPendingApps = async () => {
    try {
      const response = await axios.get(`${API}/gdrfad/pending-applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingGdrfadApps(response.data.applications || []);
    } catch (error) {
      console.error('Error fetching pending apps:', error);
    }
  };

  const fetchGdrfadSyncHistory = async () => {
    try {
      const response = await axios.get(`${API}/gdrfad/sync-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGdrfadSyncHistory(response.data.recent_syncs || []);
    } catch (error) {
      console.error('Error fetching sync history:', error);
    }
  };

  const fetchAppsNeedingVisa = async () => {
    try {
      const response = await axios.get(`${API}/gdrfad/applications-needing-visa`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppsNeedingVisa(response.data.applications || []);
    } catch (error) {
      console.error('Error fetching apps needing visa:', error);
    }
  };

  const fetchB2BAgents = async () => {
    try {
      const response = await axios.get(`${API}/admin/b2b/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setB2BAgents(response.data);
    } catch (error) {
      console.error('Error fetching B2B agents:', error);
    }
  };

  const fetchB2BApplications = async () => {
    try {
      const response = await axios.get(`${API}/admin/b2b/applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setB2BApplications(response.data);
    } catch (error) {
      console.error('Error fetching B2B applications:', error);
    }
  };

  const fetchCreditRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/b2b/credit-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreditRequests(response.data);
    } catch (error) {
      console.error('Error fetching credit requests:', error);
    }
  };

  const fetchB2BNationalityRates = async () => {
    try {
      const response = await axios.get(`${API}/admin/b2b/nationality-rates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setB2BNationalityRates(response.data);
    } catch (error) {
      console.error('Error fetching B2B nationality rates:', error);
    }
  };

  const fetchNationalityGroups = async () => {
    try {
      const response = await axios.get(`${API}/admin/b2b/nationality-groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNationalityGroups(response.data);
    } catch (error) {
      console.error('Error fetching nationality groups:', error);
    }
  };

  const fetchDocumentRequirements = async () => {
    try {
      const response = await axios.get(`${API}/admin/document-requirements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocumentRequirements(response.data.nationality_requirements || []);
    } catch (error) {
      console.error('Error fetching document requirements:', error);
    }
  };

  const fetchB2BDocumentRequirements = async () => {
    try {
      const response = await axios.get(`${API}/admin/b2b-document-requirements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setB2BDocumentRequirements(response.data.nationality_requirements || []);
    } catch (error) {
      console.error('Error fetching B2B document requirements:', error);
    }
  };

  const fetchAgentRates = async (agentId) => {
    try {
      const response = await axios.get(`${API}/admin/b2b/agents/${agentId}/rates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgentRatesData(response.data.rates || []);
    } catch (error) {
      console.error('Error fetching agent rates:', error);
      setAgentRatesData([]);
    }
  };

  const fetchAdminUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminUsers(response.data);
    } catch (error) {
      console.error('Error fetching admin users:', error);
    }
  };

  const fetchNationalityRates = async () => {
    try {
      const response = await axios.get(`${API}/admin/nationality-rates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNationalityRates(response.data.rates);
      setEditedRates(response.data.rates);
    } catch (error) {
      console.error('Error fetching nationality rates:', error);
    }
  };

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/admin/applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
    toast.success('Logged out successfully');
  };

  const openDetails = (app) => {
    setSelectedApp(app);
    setDetailsOpen(true);
  };

  const openStatusUpdate = (app) => {
    setSelectedApp(app);
    setNewStatus(app.status);
    setAdminNotes(app.admin_notes || '');
    setStatusUpdateOpen(true);
  };

  const openVisaUpload = (app, type = 'main', memberIndex = null) => {
    setSelectedApp(app);
    setUploadTarget({ type, index: memberIndex });
    setVisaFile(null);
    setUploadVisaOpen(true);
  };

  const handleStatusUpdate = async () => {
    setActionLoading(true);
    try {
      await axios.put(
        `${API}/admin/applications/${selectedApp.id}/status`,
        {
          status: newStatus,
          admin_notes: adminNotes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Status updated successfully');
      setStatusUpdateOpen(false);
      fetchApplications();
    } catch (error) {
      console.error('Status update error:', error);
      toast.error('Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVisaUpload = async () => {
    if (!visaFile) {
      toast.error('Please select a file');
      return;
    }

    setActionLoading(true);
    const formData = new FormData();
    formData.append('visa_document', visaFile);

    try {
      let url;
      if (uploadTarget.type === 'main') {
        url = `${API}/admin/applications/${selectedApp.id}/visa`;
      } else {
        url = `${API}/admin/applications/${selectedApp.id}/family/${uploadTarget.index}/visa`;
      }
      
      await axios.post(url, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const targetName = uploadTarget.type === 'main' 
        ? `${selectedApp.first_name} ${selectedApp.last_name}`
        : `Family Member ${uploadTarget.index + 1}`;
      
      toast.success(`Visa document uploaded successfully for ${targetName}`);
      setUploadVisaOpen(false);
      fetchApplications();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload visa document');
    } finally {
      setActionLoading(false);
    }
  };

  // B2B Visa Upload Functions
  const handleUploadVisa = async () => {
    if (!visaFile || !visaUploadApp) {
      toast.error('Please select a file');
      return;
    }
    
    setVisaUploading(true);
    try {
      const formData = new FormData();
      formData.append('visa_document', visaFile);
      
      const response = await axios.post(`${API}/admin/b2b/applications/${visaUploadApp.id}/visa`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Visa document uploaded successfully');
      setVisaUploadModalOpen(false);
      setVisaFile(null);
      setVisaUploadApp(null);
      fetchB2BApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload visa');
    } finally {
      setVisaUploading(false);
    }
  };

  const openVisaUploadModal = (app) => {
    setVisaUploadApp(app);
    setVisaUploadModalOpen(true);
  };

  // Super Admin Functions
  const handleCreateAdmin = async () => {
    if (!newAdminData.email || !newAdminData.password) {
      toast.error('Email and password are required');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/users/create`, newAdminData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Admin user created successfully');
      setCreateAdminOpen(false);
      setNewAdminData({ email: '', password: '', name: '' });
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword) {
      toast.error('Password is required');
      return;
    }
    setActionLoading(true);
    try {
      await axios.put(`${API}/admin/users/${selectedAdminId}/password`, {
        admin_id: selectedAdminId,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Password changed successfully');
      setChangePasswordOpen(false);
      setNewPassword('');
      setSelectedAdminId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!confirm('Are you sure you want to delete this admin?')) return;
    try {
      await axios.delete(`${API}/admin/users/${adminId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Admin deleted successfully');
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete admin');
    }
  };

  // Admin Edit B2B Application Handlers
  const handleEditB2BApplication = (app) => {
    setEditingB2BApp({
      ...app,
      first_name: app.first_name || '',
      middle_name: app.middle_name || '',
      last_name: app.last_name || '',
      passport_number: app.passport_number || '',
      date_of_issue: app.date_of_issue || '',
      date_of_expiry: app.date_of_expiry || '',
      date_of_birth: app.date_of_birth || '',
      occupation: app.occupation || '',
      nationality: app.nationality || '',
      visa_duration: app.visa_duration || '30_days',
      father_name: app.father_name || '',
      mother_name: app.mother_name || '',
      mobile: app.mobile || '',
      address: app.address || '',
      admin_notes: app.admin_notes || '',
      custom_visa_fee_per_person: app.visa_fee_per_person || 0,
    });
    setEditB2BAppOpen(true);
  };

  const handleSaveB2BAppEdit = async () => {
    if (!editingB2BApp) return;
    
    setEditB2BAppLoading(true);
    try {
      const formData = new FormData();
      
      formData.append('first_name', editingB2BApp.first_name);
      formData.append('middle_name', editingB2BApp.middle_name || '');
      formData.append('last_name', editingB2BApp.last_name);
      formData.append('passport_number', editingB2BApp.passport_number);
      formData.append('date_of_issue', editingB2BApp.date_of_issue);
      formData.append('date_of_expiry', editingB2BApp.date_of_expiry);
      formData.append('date_of_birth', editingB2BApp.date_of_birth);
      formData.append('occupation', editingB2BApp.occupation);
      formData.append('nationality', editingB2BApp.nationality);
      formData.append('visa_duration', editingB2BApp.visa_duration);
      formData.append('father_name', editingB2BApp.father_name || '');
      formData.append('mother_name', editingB2BApp.mother_name || '');
      formData.append('mobile', editingB2BApp.mobile || '');
      formData.append('address', editingB2BApp.address || '');
      formData.append('admin_notes', editingB2BApp.admin_notes || '');
      
      if (editingB2BApp.custom_visa_fee_per_person !== undefined) {
        formData.append('custom_visa_fee_per_person', editingB2BApp.custom_visa_fee_per_person);
      }
      
      if (editingB2BApp.new_passport_doc) formData.append('passport_doc', editingB2BApp.new_passport_doc);
      if (editingB2BApp.new_personal_photo_doc) formData.append('personal_photo_doc', editingB2BApp.new_personal_photo_doc);
      if (editingB2BApp.new_id_front_doc) formData.append('id_front_doc', editingB2BApp.new_id_front_doc);
      if (editingB2BApp.new_id_back_doc) formData.append('id_back_doc', editingB2BApp.new_id_back_doc);
      
      const response = await axios.put(`${API}/admin/b2b/applications/${editingB2BApp.id}/edit`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const priceChanged = response.data.price_changed;
      toast.success(`Application updated${priceChanged ? ' with new pricing' : ''}`);
      setEditB2BAppOpen(false);
      setEditingB2BApp(null);
      fetchB2BApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update application');
    } finally {
      setEditB2BAppLoading(false);
    }
  };

  const handleAddNationality = async () => {
    if (!newNationality.nationality || !newNationality.rate_30_days || !newNationality.rate_60_days) {
      toast.error('Nationality and both rates are required');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/nationality-rates/add`, {
        nationality: newNationality.nationality,
        rate_30_days: parseFloat(newNationality.rate_30_days),
        rate_60_days: parseFloat(newNationality.rate_60_days)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Nationality added successfully');
      setAddNationalityOpen(false);
      setNewNationality({ nationality: '', rate_30_days: '', rate_60_days: '' });
      fetchNationalityRates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add nationality');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveRates = async () => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/admin/nationality-rates`, {
        rates: editedRates
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rates saved successfully');
      fetchNationalityRates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save rates');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteNationality = async (nationality) => {
    if (!confirm(`Are you sure you want to delete ${nationality}?`)) return;
    try {
      await axios.delete(`${API}/admin/nationality-rates/${nationality}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Nationality deleted successfully');
      fetchNationalityRates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete nationality');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'processing':
        return <FileText className="w-4 h-4" />;
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'sent_back':
        return 'bg-amber-100 text-amber-800';
      case 'draft':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  const stats = [
    {
      title: 'Total Applications',
      value: applications.length,
      icon: <FileText className="w-6 h-6" />,
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Pending',
      value: applications.filter(a => a.status === 'pending').length,
      icon: <Clock className="w-6 h-6" />,
      color: 'bg-yellow-100 text-yellow-600'
    },
    {
      title: 'Approved',
      value: applications.filter(a => a.status === 'approved').length,
      icon: <CheckCircle className="w-6 h-6" />,
      color: 'bg-green-100 text-green-600'
    },
    {
      title: 'Processing',
      value: applications.filter(a => a.status === 'processing').length,
      icon: <FileText className="w-6 h-6" />,
      color: 'bg-blue-100 text-blue-600'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard-page">
      {/* Admin Navbar */}
      <nav className="bg-slate-900 border-b border-slate-800 text-white">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_5769230e-7a66-4b7d-9294-6503e125a0b4/artifacts/muqtjev5_IMG_5362.jpeg" 
                alt="A-One Visas Logo" 
                className="w-10 h-10 rounded-lg object-cover border border-[#009FB7]"
              />
              <div>
                <h1 className="text-xl font-heading font-bold">A-One Visas</h1>
                <p className="text-xs text-slate-400">Admin Dashboard</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium">{admin?.email}</p>
                <p className="text-xs text-slate-400">
                  {isSuperAdmin ? (
                    <span className="text-[#009FB7] font-semibold">Super Admin</span>
                  ) : (
                    'Administrator'
                  )}
                </p>
              </div>
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="text-white hover:bg-slate-800"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8">
        {/* Admin Tabs with Permission-based rendering */}
        {(isSuperAdmin || myPermissions.length > 0) ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-white border shadow-sm flex-wrap">
              {hasPermission('tab_applications') && (
                <TabsTrigger value="applications" className="data-[state=active]:bg-[#1A4D7F] data-[state=active]:text-white">
                  <FileText className="w-4 h-4 mr-2" />
                  B2C Applications
                </TabsTrigger>
              )}
              {hasPermission('tab_b2b_agents') && (
                <TabsTrigger value="b2b-agents" className="data-[state=active]:bg-[#1A4D7F] data-[state=active]:text-white">
                  <Plane className="w-4 h-4 mr-2" />
                  B2B Agents
                </TabsTrigger>
              )}
              {hasPermission('tab_b2b_applications') && (
                <TabsTrigger value="b2b-applications" className="data-[state=active]:bg-[#1A4D7F] data-[state=active]:text-white">
                  <FileText className="w-4 h-4 mr-2" />
                  B2B Applications
                </TabsTrigger>
              )}
              {hasPermission('tab_b2b_rates') && (
                <TabsTrigger value="b2b-rates" className="data-[state=active]:bg-[#1A4D7F] data-[state=active]:text-white">
                  <DollarSign className="w-4 h-4 mr-2" />
                  B2B Rates
                </TabsTrigger>
              )}
              {hasPermission('tab_credit_requests') && (
                <TabsTrigger value="credit-requests" className="data-[state=active]:bg-[#1A4D7F] data-[state=active]:text-white">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Credit Requests
                </TabsTrigger>
              )}
              {hasPermission('tab_admin_users') && (
                <TabsTrigger value="users" className="data-[state=active]:bg-[#1A4D7F] data-[state=active]:text-white">
                  <Users className="w-4 h-4 mr-2" />
                  Admin Users
                </TabsTrigger>
              )}
              {hasPermission('tab_nationality_rates') && (
                <TabsTrigger value="rates" className="data-[state=active]:bg-[#1A4D7F] data-[state=active]:text-white">
                  <DollarSign className="w-4 h-4 mr-2" />
                  B2C Rates
                </TabsTrigger>
              )}
              {hasPermission('tab_gdrfad') && (
                <TabsTrigger value="gdrfad" className="data-[state=active]:bg-[#1A4D7F] data-[state=active]:text-white">
                  <Globe className="w-4 h-4 mr-2" />
                  GDRFAD
                </TabsTrigger>
              )}
              {hasPermission('tab_statements') && (
                <TabsTrigger value="statements" className="data-[state=active]:bg-[#1A4D7F] data-[state=active]:text-white">
                  <Wallet className="w-4 h-4 mr-2" />
                  Statements
                </TabsTrigger>
              )}
              {isSuperAdmin && (
                <TabsTrigger value="permissions" className="data-[state=active]:bg-[#1A4D7F] data-[state=active]:text-white">
                  <Key className="w-4 h-4 mr-2" />
                  Permissions
                </TabsTrigger>
              )}
              {isSuperAdmin && (
                <TabsTrigger value="ninthware" className="data-[state=active]:bg-[#1A4D7F] data-[state=active]:text-white">
                  <Globe className="w-4 h-4 mr-2" />
                  Ninthware
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="applications">
              {renderApplicationsTab()}
            </TabsContent>

            <TabsContent value="b2b-agents">
              {renderB2BAgentsTab()}
            </TabsContent>

            <TabsContent value="b2b-applications">
              {renderB2BApplicationsTab()}
            </TabsContent>

            <TabsContent value="b2b-rates">
              {renderB2BRatesTab()}
            </TabsContent>

            <TabsContent value="credit-requests">
              {renderCreditRequestsTab()}
            </TabsContent>

            <TabsContent value="users">
              {renderUsersTab()}
            </TabsContent>

            <TabsContent value="rates">
              {renderRatesTab()}
            </TabsContent>

            <TabsContent value="gdrfad">
              {renderGDRFADTab()}
            </TabsContent>

            <TabsContent value="statements">
              {renderStatementsTab()}
            </TabsContent>

            <TabsContent value="permissions">
              {renderPermissionsTab()}
            </TabsContent>

            <TabsContent value="ninthware">
              <NinthwareTab token={token} />
            </TabsContent>
          </Tabs>
        ) : (
          renderApplicationsTab()
        )}
      </div>

      {renderDialogs()}
    </div>
  );

  function renderApplicationsTab() {
    return (
      <>
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">{stat.title}</p>
                      <p className="text-3xl font-heading font-bold text-[#1A4D7F]">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}>
                      {stat.icon}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Applications Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-heading">All Applications</CardTitle>
            <CardDescription>Manage and track all visa applications</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Applicant</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Visa Type</TableHead>
                    <TableHead>Applicants</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applications.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                        No applications found
                      </TableCell>
                    </TableRow>
                  ) : (
                    applications.map((app) => (
                      <TableRow key={app.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">
                          {app.first_name} {app.last_name}
                        </TableCell>
                        <TableCell>{app.user_email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            {app.visa_duration === '60_days' ? '60 Days' : '30 Days'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4 text-slate-500" />
                            <span className="font-medium">{app.total_applicants || 1}</span>
                            {(app.family_members?.length > 0) && (
                              <span className="text-xs text-slate-500">
                                (+{app.family_members.length} family)
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getStatusColor(app.status)} flex items-center gap-1 w-fit`}>
                            {getStatusIcon(app.status)}
                            {app.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={app.payment_status === 'paid' ? 'default' : 'outline'}>
                            {app.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {new Date(app.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openDetails(app)}
                              data-testid={`view-details-${app.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openStatusUpdate(app)}
                              data-testid={`update-status-${app.id}`}
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openVisaUpload(app)}
                              data-testid={`upload-visa-${app.id}`}
                            >
                              <Upload className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </>
    );
  }

  function renderUsersTab() {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-2xl font-heading">Admin Users</CardTitle>
            <CardDescription>Manage administrator accounts</CardDescription>
          </div>
          <Button onClick={() => setCreateAdminOpen(true)} className="bg-[#009FB7] hover:bg-[#008fa5]">
            <UserPlus className="w-4 h-4 mr-2" />
            Add Admin
          </Button>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {adminUsers.map((adminUser) => (
                  <TableRow key={adminUser.id} className="hover:bg-slate-50">
                    <TableCell className="font-medium">{adminUser.email}</TableCell>
                    <TableCell>{adminUser.name || '-'}</TableCell>
                    <TableCell>
                      {adminUser.is_super_admin ? (
                        <Badge className="bg-purple-100 text-purple-800">Super Admin</Badge>
                      ) : (
                        <Badge variant="outline">Admin</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {adminUser.created_at ? new Date(adminUser.created_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedAdminId(adminUser.id);
                            setChangePasswordOpen(true);
                          }}
                          title="Change Password"
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        {!adminUser.is_super_admin && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteAdmin(adminUser.id)}
                            title="Delete Admin"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    );
  }

  function renderRatesTab() {
    return (
      <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-2xl font-heading">Nationality Rates</CardTitle>
            <CardDescription>Manage visa fees by nationality (in AED) for 30-day and 60-day visas</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setAddNationalityOpen(true)} variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Add Nationality
            </Button>
            <Button onClick={handleSaveRates} disabled={actionLoading} className="bg-[#009FB7] hover:bg-[#008fa5]">
              <Save className="w-4 h-4 mr-2" />
              {actionLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Header */}
            <div className="grid grid-cols-4 gap-4 px-4 py-2 bg-slate-100 rounded-lg font-medium text-sm text-slate-600">
              <div>Nationality</div>
              <div className="text-center">30 Days (AED)</div>
              <div className="text-center">60 Days (AED)</div>
              <div className="text-right">Actions</div>
            </div>
            
            {Object.entries(editedRates).sort().map(([nationality, rates]) => {
              const rate30 = typeof rates === 'object' ? (rates['30_days'] || 500) : rates;
              const rate60 = typeof rates === 'object' ? (rates['60_days'] || 750) : (rates * 1.5);
              
              return (
                <div key={nationality} className="grid grid-cols-4 gap-4 items-center p-4 bg-slate-50 rounded-lg border">
                  <div className="font-medium text-slate-700">{nationality}</div>
                  <div className="flex items-center justify-center gap-2">
                    <Input
                      type="number"
                      value={rate30}
                      onChange={(e) => setEditedRates({
                        ...editedRates,
                        [nationality]: {
                          ...((typeof rates === 'object') ? rates : {}),
                          '30_days': parseFloat(e.target.value) || 0,
                          '60_days': rate60
                        }
                      })}
                      className="w-24 text-center"
                    />
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <Input
                      type="number"
                      value={rate60}
                      onChange={(e) => setEditedRates({
                        ...editedRates,
                        [nationality]: {
                          ...((typeof rates === 'object') ? rates : {}),
                          '30_days': rate30,
                          '60_days': parseFloat(e.target.value) || 0
                        }
                      })}
                      className="w-24 text-center"
                    />
                  </div>
                  <div className="flex justify-end">
                    {nationality !== 'Other' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDeleteNationality(nationality)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Document Requirements Section */}
      <Card className="mt-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-xl font-heading">Document Requirements by Nationality</CardTitle>
            <CardDescription>Configure which documents are required for each nationality</CardDescription>
          </div>
          <Button onClick={() => setAddDocReqOpen(true)} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-2" />
            Set Requirements
          </Button>
        </CardHeader>
        <CardContent>
          {documentRequirements.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No custom document requirements set.</p>
              <p className="text-sm">All nationalities use default: Passport Copy, Personal Picture, ID Card Front</p>
            </div>
          ) : (
            <div className="space-y-3">
              {documentRequirements.map((req) => (
                <div key={req.nationality} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                  <div>
                    <p className="font-medium">{req.nationality}</p>
                    <div className="flex gap-2 mt-1">
                      {req.documents?.map((doc) => (
                        <span 
                          key={doc.id} 
                          className={`text-xs px-2 py-1 rounded ${doc.required ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500 line-through'}`}
                        >
                          {doc.name}
                        </span>
                      ))}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-500 hover:text-red-700"
                    onClick={async () => {
                      if (window.confirm(`Remove custom requirements for ${req.nationality}? They will use defaults.`)) {
                        try {
                          await axios.delete(`${API}/admin/document-requirements/${encodeURIComponent(req.nationality)}`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success(`Custom requirements removed for ${req.nationality}`);
                          fetchDocumentRequirements();
                        } catch (error) {
                          toast.error('Failed to remove requirements');
                        }
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Document Requirements Dialog */}
      <Dialog open={addDocReqOpen} onOpenChange={setAddDocReqOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Document Requirements</DialogTitle>
            <DialogDescription>Configure which documents are required for specific nationalities</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nationalities (comma-separated) *</Label>
              <Input
                value={docReqData.nationalities}
                onChange={(e) => setDocReqData({ ...docReqData, nationalities: e.target.value })}
                placeholder="South Africa, Nigeria, Ghana"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Enter nationalities to apply these requirements to</p>
            </div>
            <div className="pt-4 border-t">
              <Label className="mb-3 block">Required Documents</Label>
              <div className="space-y-3">
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={docReqData.passport_photo}
                    onChange={(e) => setDocReqData({ ...docReqData, passport_photo: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <div>
                    <p className="font-medium">Passport Photo Page</p>
                    <p className="text-xs text-slate-500">Always recommended</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={docReqData.personal_photo}
                    onChange={(e) => setDocReqData({ ...docReqData, personal_photo: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <div>
                    <p className="font-medium">Personal Photo</p>
                    <p className="text-xs text-slate-500">Passport-size photo</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={docReqData.id_front}
                    onChange={(e) => setDocReqData({ ...docReqData, id_front: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <div>
                    <p className="font-medium">ID Card Front</p>
                    <p className="text-xs text-slate-500">National ID card front side</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={docReqData.id_back}
                    onChange={(e) => setDocReqData({ ...docReqData, id_back: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <div>
                    <p className="font-medium">ID Card Back</p>
                    <p className="text-xs text-slate-500">Back side of national ID</p>
                  </div>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDocReqOpen(false)}>Cancel</Button>
            <Button 
              onClick={async () => {
                if (!docReqData.nationalities) {
                  toast.error('Enter at least one nationality');
                  return;
                }
                setActionLoading(true);
                try {
                  const formData = new FormData();
                  formData.append('nationalities', docReqData.nationalities);
                  formData.append('passport_photo', docReqData.passport_photo);
                  formData.append('personal_photo', docReqData.personal_photo);
                  formData.append('id_front', docReqData.id_front);
                  formData.append('id_back', docReqData.id_back);
                  
                  await axios.post(`${API}/admin/document-requirements/bulk`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  toast.success('Document requirements set');
                  setAddDocReqOpen(false);
                  setDocReqData({
                    nationalities: '',
                    passport_photo: true,
                    personal_photo: true,
                    id_front: true,
                    id_back: false
                  });
                  fetchDocumentRequirements();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to set requirements');
                } finally {
                  setActionLoading(false);
                }
              }}
              disabled={actionLoading}
              className="bg-purple-600"
            >
              {actionLoading ? 'Saving...' : 'Save Requirements'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
    );
  }

  function renderGDRFADTab() {
    const handleUpdateStatus = async () => {
      if (!selectedGdrfadApp) return;
      setGdrfadLoading(true);
      try {
        await axios.post(`${API}/gdrfad/manual-update`, {
          application_id: selectedGdrfadApp.id,
          application_type: selectedGdrfadApp.type,
          new_status: gdrfadUpdateStatus,
          gdrfad_reference: gdrfadUpdateRef,
          notes: gdrfadUpdateNotes
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Application ${selectedGdrfadApp.id.slice(0,8)}... updated to ${gdrfadUpdateStatus}`);
        setGdrfadUpdateModalOpen(false);
        setSelectedGdrfadApp(null);
        setGdrfadUpdateRef('');
        setGdrfadUpdateNotes('');
        fetchGdrfadPendingApps();
        fetchGdrfadSyncHistory();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update status');
      } finally {
        setGdrfadLoading(false);
      }
    };

    const openUpdateModal = (app) => {
      setSelectedGdrfadApp(app);
      setGdrfadUpdateStatus('approved');
      setGdrfadUpdateRef(app.gdrfad_reference || '');
      setGdrfadUpdateNotes('');
      setGdrfadUpdateModalOpen(true);
    };

    const getStatusColor = (status) => {
      const colors = {
        pending: 'bg-yellow-100 text-yellow-800',
        processing: 'bg-blue-100 text-blue-800',
        approved: 'bg-green-100 text-green-800',
        rejected: 'bg-red-100 text-red-800',
      };
      return colors[status] || 'bg-slate-100 text-slate-800';
    };

    // Automation handlers
    const fetchAutoSyncStatus = fetchAutoSyncStatusGlobal;

    const handleStartAutoSync = async () => {
      setAutoSyncLoading(true);
      try {
        const response = await axios.post(`${API}/gdrfad/auto-sync/login`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAutoSyncResults(response.data);
        if (response.data.needs_otp) {
          toast.info('Google Authenticator OTP required. Please enter the code below.');
        } else if (response.data.success) {
          toast.success('Auto-login successful! Browser window should be visible.');
        } else {
          toast.error(`Login failed: ${response.data.message}`);
        }
        fetchAutoSyncStatus();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to start auto-sync');
      } finally {
        setAutoSyncLoading(false);
      }
    };

    const handleSubmitOtp = async () => {
      if (!otpInput || otpInput.length < 6) {
        toast.error('Please enter a valid 6-digit OTP code');
        return;
      }
      setAutoSyncLoading(true);
      try {
        const response = await axios.post(`${API}/gdrfad/auto-sync/submit-otp`, 
          { otp_code: otpInput },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAutoSyncResults(response.data);
        if (response.data.success) {
          toast.success('OTP verified! Login successful.');
          setOtpInput('');
        } else {
          toast.error(`OTP verification failed: ${response.data.message}`);
        }
        fetchAutoSyncStatus();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'OTP submission failed');
      } finally {
        setAutoSyncLoading(false);
      }
    };

    const handleReLogin = async () => {
      setAutoSyncLoading(true);
      try {
        // First stop the current browser session
        await axios.post(`${API}/gdrfad/auto-sync/stop`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Then start a new login
        const response = await axios.post(`${API}/gdrfad/auto-sync/login`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAutoSyncResults(response.data);
        if (response.data.success) {
          if (response.data.needs_otp) {
            toast.info('Re-login started. Please enter your Google Authenticator OTP.');
          } else {
            toast.success('Re-login successful!');
          }
        } else {
          toast.error(`Re-login failed: ${response.data.message}`);
        }
        fetchAutoSyncStatus();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Re-login failed');
      } finally {
        setAutoSyncLoading(false);
      }
    };

    const handleRunAutoSync = async () => {
      setAutoSyncLoading(true);
      try {
        const response = await axios.post(`${API}/gdrfad/auto-sync/run`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAutoSyncResults(response.data);
        toast.success(`Sync complete: ${response.data.updated} applications updated`);
        fetchAutoSyncStatus();
        fetchGdrfadPendingApps();
        fetchGdrfadSyncHistory();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Auto-sync failed');
      } finally {
        setAutoSyncLoading(false);
      }
    };

    const handleFetchGdrfadVisas = async () => {
      setGdrfadSyncing(true);
      try {
        const response = await axios.post(`${API}/gdrfad/auto-sync/fetch-applications`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Check for session expired response
        if (response.data.session_expired) {
          toast.error('Session expired. Please login again.');
          fetchAutoSyncStatus();
          return;
        }
        
        setGdrfadVisas(response.data);
        const matched = response.data.total_matched || 0;
        const total = response.data.total_in_gdrfad || 0;
        toast.success(`Found ${total} visas in GDRFAD, ${matched} matched with our database`);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to fetch GDRFAD visas');
      } finally {
        setGdrfadSyncing(false);
      }
    };

    const handleSyncGdrfadToDatabase = async () => {
      setGdrfadSyncing(true);
      try {
        const response = await axios.post(`${API}/gdrfad/auto-sync/sync-to-database`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Check for session expired response
        if (response.data.session_expired) {
          toast.error('Session expired. Please login again.');
          fetchAutoSyncStatus();
          return;
        }
        
        toast.success(`Synced ${response.data.updated || 0} applications to database`);
        fetchB2BApplications();
        fetchApplications();
        // Refresh the GDRFAD data
        handleFetchGdrfadVisas();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to sync to database');
      } finally {
        setGdrfadSyncing(false);
      }
    };

    const handleDownloadVisas = async () => {
      setGdrfadSyncing(true);
      try {
        const response = await axios.post(`${API}/gdrfad/auto-sync/download-visas`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Check for session expired response
        if (response.data.session_expired) {
          toast.error('Session expired. Please login again.');
          fetchAutoSyncStatus();
          return;
        }
        
        toast.success(`Downloaded ${response.data.downloaded || 0} visa documents`);
        fetchB2BApplications();
        fetchApplications();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to download visas');
      } finally {
        setGdrfadSyncing(false);
      }
    };

    const handleDownloadSingleVisa = async () => {
      if (!singlePassportDownload.trim()) {
        toast.error('Please enter a passport number');
        return;
      }
      
      setSingleDownloadLoading(true);
      try {
        const response = await axios.post(`${API}/gdrfad/auto-sync/download-single`, 
          { passport_number: singlePassportDownload.trim() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Check for session expired response
        if (response.data.session_expired) {
          toast.error('Session expired. Please login again.');
          fetchAutoSyncStatus();
          return;
        }
        
        if (response.data.success) {
          toast.success(`Downloaded visa for ${response.data.passport}. File size: ${(response.data.file_size / 1024).toFixed(1)} KB`);
          setSinglePassportDownload('');
          fetchB2BApplications();
          fetchApplications();
        } else {
          toast.error(response.data.message || 'Failed to download visa');
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to download visa');
      } finally {
        setSingleDownloadLoading(false);
      }
    };

    const handleTestNavigation = async () => {
      setAutoSyncLoading(true);
      try {
        const response = await axios.post(`${API}/gdrfad/auto-sync/navigate`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Navigation test complete. Check screenshots.');
        // Fetch screenshots
        const screenshotsRes = await axios.get(`${API}/gdrfad/auto-sync/screenshots`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (screenshotsRes.data.screenshots?.length > 0) {
          const latestScreenshot = screenshotsRes.data.screenshots[0];
          window.open(`${BACKEND_URL}${latestScreenshot.url}`, '_blank');
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Navigation test failed');
      } finally {
        setAutoSyncLoading(false);
      }
    };

    const handleStartScheduler = async () => {
      setAutoSyncLoading(true);
      try {
        const response = await axios.post(`${API}/gdrfad/scheduler/start?interval_minutes=60`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Hourly scheduler started!');
        fetchAutoSyncStatus();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to start scheduler');
      } finally {
        setAutoSyncLoading(false);
      }
    };

    const handleStopAutoSync = async () => {
      setAutoSyncLoading(true);
      try {
        await axios.post(`${API}/gdrfad/auto-sync/stop`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        await axios.post(`${API}/gdrfad/scheduler/stop`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Automation stopped');
        fetchAutoSyncStatus();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to stop');
      } finally {
        setAutoSyncLoading(false);
      }
    };

    return (
      <div className="space-y-6">
        {/* Auto-Sync Mode */}
        <Card className="border-green-500 border-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-green-500" />
              GDRFAD Auto-Sync (2Captcha Enabled)
            </CardTitle>
            <CardDescription>
              Automatically logs into GDRFAD, solves CAPTCHA, and syncs application statuses.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Status Display */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-500">Browser</p>
                  <p className={`font-medium ${autoSyncStatus?.browser_running ? 'text-green-600' : 'text-slate-400'}`}>
                    {autoSyncStatus?.browser_running ? 'Running' : 'Stopped'}
                  </p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-500">Mode</p>
                  <p className={`font-medium ${autoSyncStatus?.headless_mode ? 'text-blue-600' : 'text-purple-600'}`}>
                    {autoSyncStatus?.headless_mode ? 'Headless' : 'Visible'}
                  </p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-500">Login Status</p>
                  <p className={`font-medium ${autoSyncStatus?.is_logged_in ? 'text-green-600' : 'text-orange-500'}`}>
                    {autoSyncStatus?.is_logged_in ? 'Logged In' : 'Not Logged In'}
                  </p>
                  {autoSyncStatus?.browser_running && autoSyncStatus?.session_valid === false && (
                    <p className="text-xs text-red-500 mt-1">Session Expired!</p>
                  )}
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-500">Scheduler</p>
                  <p className={`font-medium ${autoSyncStatus?.scheduler_running ? 'text-green-600' : 'text-slate-400'}`}>
                    {autoSyncStatus?.scheduler_running ? 'Active (Hourly)' : 'Inactive'}
                  </p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-500">Last Sync</p>
                  <p className="font-medium text-sm">
                    {autoSyncStatus?.last_auto_sync 
                      ? new Date(autoSyncStatus.last_auto_sync).toLocaleString() 
                      : 'Never'}
                  </p>
                </div>
              </div>

              {/* Control Buttons */}
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={handleStartAutoSync}
                  disabled={autoSyncLoading || autoSyncStatus?.is_logged_in}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {autoSyncLoading ? 'Starting...' : 'Start & Login'}
                </Button>
                {/* Re-Login Button - Shows when session expired */}
                {autoSyncStatus?.browser_running && autoSyncStatus?.session_valid === false && (
                  <Button 
                    onClick={handleReLogin}
                    disabled={autoSyncLoading}
                    className="bg-orange-500 hover:bg-orange-600"
                    data-testid="gdrfad-relogin-btn"
                  >
                    {autoSyncLoading ? 'Re-logging in...' : 'Re-Login (Session Expired)'}
                  </Button>
                )}
                <Button 
                  onClick={handleTestNavigation}
                  disabled={autoSyncLoading || !autoSyncStatus?.is_logged_in}
                  variant="outline"
                  className="border-purple-600 text-purple-600"
                >
                  Test Navigation
                </Button>
                <Button 
                  onClick={handleRunAutoSync}
                  disabled={autoSyncLoading}
                  variant="outline"
                  className="border-green-600 text-green-600"
                >
                  Run Sync Now
                </Button>
                <Button 
                  onClick={handleStartScheduler}
                  disabled={autoSyncLoading || autoSyncStatus?.scheduler_running}
                  variant="outline"
                  className="border-blue-600 text-blue-600"
                >
                  Start Hourly Scheduler
                </Button>
                <Button 
                  onClick={handleStopAutoSync}
                  disabled={autoSyncLoading}
                  variant="outline"
                  className="border-red-500 text-red-500"
                >
                  Stop All
                </Button>
                <Button 
                  onClick={fetchAutoSyncStatus}
                  variant="ghost"
                  size="sm"
                >
                  <RefreshCcw className="w-4 h-4" />
                </Button>
              </div>

              {/* Google Authenticator OTP Input */}
              {(autoSyncResults?.needs_otp || (autoSyncStatus?.browser_running && !autoSyncStatus?.is_logged_in)) && (
                <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
                  <h4 className="font-medium text-orange-800 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Google Authenticator OTP Required
                  </h4>
                  <p className="text-sm text-orange-700 mb-3">
                    Open your Google Authenticator app and enter the 6-digit code for the GDRFAD account.
                  </p>
                  <div className="flex gap-3 items-center">
                    <Input
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      value={otpInput}
                      onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-40 font-mono text-lg text-center tracking-widest"
                      maxLength={6}
                      data-testid="gdrfad-otp-input"
                    />
                    <Button 
                      onClick={handleSubmitOtp}
                      disabled={autoSyncLoading || otpInput.length < 6}
                      className="bg-orange-600 hover:bg-orange-700"
                      data-testid="gdrfad-submit-otp-btn"
                    >
                      {autoSyncLoading ? 'Verifying...' : 'Submit OTP'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Results Display */}
              {autoSyncResults && (
                <div className="bg-slate-50 border rounded-lg p-4">
                  <h4 className="font-medium mb-2">Last Sync Results</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Checked:</span>{' '}
                      <span className="font-medium">{autoSyncResults.total_checked || 0}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Updated:</span>{' '}
                      <span className="font-medium text-green-600">{autoSyncResults.updated || 0}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Errors:</span>{' '}
                      <span className="font-medium text-red-600">{autoSyncResults.errors?.length || 0}</span>
                    </div>
                  </div>
                  {autoSyncResults.updates?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-slate-500 mb-1">Updates:</p>
                      <div className="max-h-32 overflow-y-auto text-xs space-y-1">
                        {autoSyncResults.updates.map((u, i) => (
                          <div key={i} className="bg-white p-2 rounded">
                            {u.passport}: {u.old_status} → <span className="text-green-600 font-medium">{u.new_status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Info Note */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
                <p className="text-yellow-800">
                  <strong>Note:</strong> Browser runs in visible mode for monitoring. A browser window will open on the server.
                  The system will automatically solve CAPTCHAs using 2Captcha service.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* GDRFAD Visas List */}
        {autoSyncStatus?.is_logged_in && (
          <Card className="border-blue-500 border-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" />
                GDRFAD Visa Applications
              </CardTitle>
              <CardDescription>
                View and sync visa statuses from GDRFAD portal to your database
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex gap-3 flex-wrap">
                  <Button 
                    onClick={handleFetchGdrfadVisas}
                    disabled={gdrfadSyncing}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {gdrfadSyncing ? 'Fetching...' : 'Fetch Visas from GDRFAD'}
                  </Button>
                  {gdrfadVisas?.matched?.length > 0 && (
                    <Button 
                      onClick={handleSyncGdrfadToDatabase}
                      disabled={gdrfadSyncing}
                      variant="outline"
                      className="border-green-600 text-green-600"
                    >
                      {gdrfadSyncing ? 'Syncing...' : `Sync ${gdrfadVisas.matched.filter(m => m.needs_update).length} Updates to Database`}
                    </Button>
                  )}
                  {gdrfadVisas?.matched?.some(m => m.gdrfad_status === 'approved') && (
                    <Button 
                      onClick={handleDownloadVisas}
                      disabled={gdrfadSyncing}
                      variant="outline"
                      className="border-purple-600 text-purple-600"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {gdrfadSyncing ? 'Downloading...' : 'Download Approved Visas'}
                    </Button>
                  )}
                </div>

                {/* Download Single Visa by Passport */}
                {autoSyncStatus?.is_logged_in && (
                  <div className="flex gap-2 items-center bg-purple-50 p-3 rounded-lg border border-purple-200">
                    <span className="text-sm font-medium text-purple-700">Download Single Visa:</span>
                    <Input
                      placeholder="Enter passport number (e.g., ZE4166631)"
                      value={singlePassportDownload}
                      onChange={(e) => setSinglePassportDownload(e.target.value.toUpperCase())}
                      className="max-w-xs font-mono"
                      data-testid="single-passport-download-input"
                    />
                    <Button 
                      onClick={handleDownloadSingleVisa}
                      disabled={singleDownloadLoading || !singlePassportDownload.trim()}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="single-passport-download-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {singleDownloadLoading ? 'Downloading...' : 'Download'}
                    </Button>
                  </div>
                )}

                {/* Summary Stats */}
                {gdrfadVisas?.total_in_gdrfad > 0 && (
                  <div className="grid grid-cols-3 gap-4 bg-slate-50 p-4 rounded-lg">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">{gdrfadVisas.total_in_gdrfad}</p>
                      <p className="text-xs text-slate-500">Total in GDRFAD</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{gdrfadVisas.total_matched}</p>
                      <p className="text-xs text-slate-500">Matched in DB</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-orange-600">{gdrfadVisas.total_unmatched}</p>
                      <p className="text-xs text-slate-500">Not in DB</p>
                    </div>
                  </div>
                )}

                {/* Matched Applications - Need Update */}
                {gdrfadVisas?.matched?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-green-700 mb-2 flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      Matched Applications ({gdrfadVisas.matched.length})
                    </h4>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-green-50">
                            <TableHead>Passport</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>GDRFAD Status</TableHead>
                            <TableHead>Our Status</TableHead>
                            <TableHead>Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {gdrfadVisas.matched.map((app, index) => (
                            <TableRow key={index} className={app.needs_update ? 'bg-yellow-50' : ''}>
                              <TableCell className="font-mono font-medium">{app.passport}</TableCell>
                              <TableCell className="text-sm">{app.name || 'N/A'}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className={app.is_done_base ? 'border-purple-500 text-purple-600' : ''}>
                                  {app.type}{app.is_done_base ? ' (Done Base)' : ''}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge className={
                                  app.gdrfad_status === 'approved' ? 'bg-green-500' :
                                  app.gdrfad_status === 'in_process' ? 'bg-blue-500' :
                                  app.gdrfad_status === 'draft' ? 'bg-gray-500' : 'bg-slate-500'
                                }>
                                  {app.gdrfad_status?.toUpperCase()}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">{app.local_status?.toUpperCase()}</Badge>
                              </TableCell>
                              <TableCell>
                                {app.needs_update ? (
                                  <span className="text-xs text-orange-600 font-medium">Needs Update</span>
                                ) : (
                                  <span className="text-xs text-green-600">In Sync</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Unmatched Applications */}
                {gdrfadVisas?.unmatched?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-orange-700 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Not in Our Database ({gdrfadVisas.unmatched.length})
                    </h4>
                    <div className="border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-orange-50">
                            <TableHead>Passport</TableHead>
                            <TableHead>GDRFAD Status</TableHead>
                            <TableHead>Details</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {gdrfadVisas.unmatched.map((app, index) => (
                            <TableRow key={index}>
                              <TableCell className="font-mono font-medium">{app.passport}</TableCell>
                              <TableCell>
                                <Badge className={
                                  app.gdrfad_status === 'approved' ? 'bg-green-500' :
                                  app.gdrfad_status === 'in_process' ? 'bg-blue-500' : 'bg-slate-500'
                                }>
                                  {app.gdrfad_status?.toUpperCase()}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs text-slate-500 max-w-[200px] truncate">
                                {app.raw_text?.substring(0, 100)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {!gdrfadVisas?.total_in_gdrfad && (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No visas loaded. Click "Fetch Visas from GDRFAD" to load applications.</p>
                    <p className="text-xs mt-1">Note: GDRFAD shows last 60 days by default</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Manual Mode Instructions */}
        <Card className="border-[#009FB7] border-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#009FB7]" />
              GDRFAD Manual Sync Mode
            </CardTitle>
            <CardDescription>
              Alternatively, log into GDRFAD manually and update statuses here.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">How to sync manually:</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                <li>Click the button below to open GDRFAD portal in a new tab</li>
                <li>Log in with your credentials (Falak01) and complete the CAPTCHA</li>
                <li>Search for each passport number from the list below</li>
                <li>Click "Update" on each application to record the status from GDRFAD</li>
              </ol>
              <div className="mt-4">
                <Button 
                  onClick={() => window.open('https://smart.gdrfad.gov.ae', '_blank')}
                  className="bg-[#009FB7] hover:bg-[#008fa5]"
                >
                  <Globe className="w-4 h-4 mr-2" />
                  Open GDRFAD Portal
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending Applications */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Pending Applications ({pendingGdrfadApps.length})</CardTitle>
                <CardDescription>Applications waiting for GDRFAD status update</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchGdrfadPendingApps}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {pendingGdrfadApps.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No pending applications to sync
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Applicant</TableHead>
                      <TableHead>Passport</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Nationality</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>GDRFAD Ref</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingGdrfadApps.map((app) => (
                      <TableRow key={app.id}>
                        <TableCell className="font-medium">
                          {app.first_name} {app.last_name}
                          {app.type === 'b2b' && (
                            <span className="block text-xs text-slate-500">{app.agent_company}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <code className="bg-slate-100 px-2 py-1 rounded text-sm font-mono">
                            {app.passport_number}
                          </code>
                        </TableCell>
                        <TableCell>
                          <Badge variant={app.type === 'b2b' ? 'default' : 'secondary'}>
                            {app.type.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>{app.nationality}</TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(app.status)}>{app.status}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {app.gdrfad_reference || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            size="sm" 
                            onClick={() => openUpdateModal(app)}
                            className="bg-[#1A4D7F]"
                          >
                            Update Status
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Sync History */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Updates</CardTitle>
            <CardDescription>Applications recently synced from GDRFAD</CardDescription>
          </CardHeader>
          <CardContent>
            {gdrfadSyncHistory.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No sync history yet
              </div>
            ) : (
              <div className="space-y-2">
                {gdrfadSyncHistory.slice(0, 10).map((app) => (
                  <div key={app.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <span className="font-medium">{app.first_name} {app.last_name}</span>
                      <span className="text-slate-500 text-sm ml-2">({app.passport_number})</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={getStatusColor(app.status)}>{app.status}</Badge>
                      <span className="text-xs text-slate-500">
                        {new Date(app.gdrfad_sync_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Manual Visa Upload Section */}
        <Card className="border-purple-500 border-2">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-purple-500" />
                  Manual GDRFA Visa Upload
                </CardTitle>
                <CardDescription>
                  Upload visa documents for approved applications that don't have a visa document yet
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchAppsNeedingVisa}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {appsNeedingVisa.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                <p>All approved applications have visa documents uploaded</p>
              </div>
            ) : (
              <div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4 text-sm">
                  <p className="text-purple-800">
                    <strong>Note:</strong> These are approved applications without visa documents. 
                    Download the visa from GDRFA portal and upload it here manually.
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-purple-50">
                        <TableHead>Applicant</TableHead>
                        <TableHead>Passport</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Nationality</TableHead>
                        <TableHead>Agent/Email</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {appsNeedingVisa.map((app) => (
                        <TableRow key={app.id} className="hover:bg-purple-50/50">
                          <TableCell className="font-medium">
                            {app.first_name} {app.last_name}
                          </TableCell>
                          <TableCell>
                            <code className="bg-slate-100 px-2 py-1 rounded text-sm font-mono">
                              {app.passport_number}
                            </code>
                          </TableCell>
                          <TableCell>
                            <Badge variant={app.type === 'b2b' ? 'default' : 'secondary'}>
                              {app.type.toUpperCase()}
                            </Badge>
                          </TableCell>
                          <TableCell>{app.nationality}</TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {app.agent_company || app.email || '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button 
                              size="sm" 
                              onClick={() => {
                                setGdrfaManualVisaApp(app);
                                setGdrfaVisaFile(null);
                                setGdrfaManualVisaUploadOpen(true);
                              }}
                              className="bg-purple-600 hover:bg-purple-700"
                              data-testid={`upload-manual-visa-${app.id}`}
                            >
                              <Upload className="w-4 h-4 mr-2" />
                              Upload Visa
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Manual Visa Upload Modal */}
        <Dialog open={gdrfaManualVisaUploadOpen} onOpenChange={setGdrfaManualVisaUploadOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload GDRFA Visa Document</DialogTitle>
              <DialogDescription>
                Upload the visa document downloaded from GDRFA portal
              </DialogDescription>
            </DialogHeader>
            {gdrfaManualVisaApp && (
              <div className="space-y-4">
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p><strong>Applicant:</strong> {gdrfaManualVisaApp.first_name} {gdrfaManualVisaApp.last_name}</p>
                  <p><strong>Passport:</strong> <code className="bg-white px-2 py-1 rounded">{gdrfaManualVisaApp.passport_number}</code></p>
                  <p><strong>Type:</strong> {gdrfaManualVisaApp.type.toUpperCase()}</p>
                  {gdrfaManualVisaApp.type === 'b2b' && gdrfaManualVisaApp.agent_company && (
                    <p><strong>Agent:</strong> {gdrfaManualVisaApp.agent_company}</p>
                  )}
                </div>
                <div>
                  <Label>Visa Document *</Label>
                  <Input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={(e) => setGdrfaVisaFile(e.target.files?.[0] || null)}
                    className="mt-1"
                    data-testid="gdrfa-visa-file-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Accepted formats: PDF, PNG, JPG, JPEG</p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setGdrfaManualVisaUploadOpen(false);
                  setGdrfaVisaFile(null);
                  setGdrfaManualVisaApp(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={async () => {
                  if (!gdrfaVisaFile || !gdrfaManualVisaApp) {
                    toast.error('Please select a file');
                    return;
                  }
                  setGdrfaVisaUploading(true);
                  try {
                    const formData = new FormData();
                    formData.append('visa_document', gdrfaVisaFile);
                    formData.append('application_id', gdrfaManualVisaApp.id);
                    formData.append('application_type', gdrfaManualVisaApp.type);
                    
                    const response = await axios.post(`${API}/gdrfad/manual-visa-upload`, formData, {
                      headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                      }
                    });
                    
                    toast.success(response.data.message || 'Visa uploaded successfully');
                    setGdrfaManualVisaUploadOpen(false);
                    setGdrfaVisaFile(null);
                    setGdrfaManualVisaApp(null);
                    fetchAppsNeedingVisa();
                    fetchB2BApplications();
                    fetchApplications();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to upload visa');
                  } finally {
                    setGdrfaVisaUploading(false);
                  }
                }}
                disabled={gdrfaVisaUploading || !gdrfaVisaFile}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="gdrfa-visa-upload-submit"
              >
                {gdrfaVisaUploading ? 'Uploading...' : 'Upload Visa'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Update Status Modal */}
        <Dialog open={gdrfadUpdateModalOpen} onOpenChange={setGdrfadUpdateModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Application Status</DialogTitle>
              <DialogDescription>
                Enter the status from GDRFAD for this application
              </DialogDescription>
            </DialogHeader>
            {selectedGdrfadApp && (
              <div className="space-y-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p><strong>Applicant:</strong> {selectedGdrfadApp.first_name} {selectedGdrfadApp.last_name}</p>
                  <p><strong>Passport:</strong> <code className="bg-white px-2 py-1 rounded">{selectedGdrfadApp.passport_number}</code></p>
                  <p><strong>Type:</strong> {selectedGdrfadApp.type.toUpperCase()}</p>
                </div>
                <div>
                  <Label>GDRFAD Status *</Label>
                  <Select value={gdrfadUpdateStatus} onValueChange={setGdrfadUpdateStatus}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="processing">Processing</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>GDRFAD Reference Number</Label>
                  <Input
                    value={gdrfadUpdateRef}
                    onChange={(e) => setGdrfadUpdateRef(e.target.value)}
                    placeholder="Enter reference from GDRFAD"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Notes (optional)</Label>
                  <Textarea
                    value={gdrfadUpdateNotes}
                    onChange={(e) => setGdrfadUpdateNotes(e.target.value)}
                    placeholder="Any additional notes..."
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setGdrfadUpdateModalOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleUpdateStatus} 
                disabled={gdrfadLoading}
                className="bg-[#009FB7]"
              >
                {gdrfadLoading ? 'Updating...' : 'Update Status'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  function renderB2BAgentsTab() {
    const handleCreateAgent = async () => {
      if (!newAgentData.email || !newAgentData.password || !newAgentData.company_name) {
        toast.error('Email, password, and company name are required');
        return;
      }
      setActionLoading(true);
      try {
        const formData = new FormData();
        Object.keys(newAgentData).forEach(key => formData.append(key, newAgentData[key]));
        
        await axios.post(`${API}/admin/b2b/agents`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Agent created successfully');
        setCreateAgentOpen(false);
        setNewAgentData({ email: '', password: '', company_name: '', contact_person: '', phone: '' });
        fetchB2BAgents();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to create agent');
      } finally {
        setActionLoading(false);
      }
    };

    const handleAddCredit = async () => {
      if (!addCreditAmount || parseFloat(addCreditAmount) <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }
      setActionLoading(true);
      try {
        const formData = new FormData();
        formData.append('amount', addCreditAmount);
        formData.append('notes', addCreditNotes);
        
        await axios.post(`${API}/admin/b2b/agents/${selectedAgent.id}/add-credit`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Credit added successfully');
        setAddCreditOpen(false);
        setAddCreditAmount('');
        setAddCreditNotes('');
        fetchB2BAgents();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to add credit');
      } finally {
        setActionLoading(false);
      }
    };

    const handleSaveAgentRates = async () => {
      setActionLoading(true);
      try {
        await axios.put(`${API}/admin/b2b/agents/${selectedAgent.id}/rates`, agentRates, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        toast.success('Rates updated successfully');
        setEditAgentRatesOpen(false);
        fetchB2BAgents();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update rates');
      } finally {
        setActionLoading(false);
      }
    };

    const handleDeductBalance = async () => {
      if (!deductAmount || parseFloat(deductAmount) <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }
      if (!deductReason || deductReason.trim().length < 3) {
        toast.error('Please provide a reason for the deduction');
        return;
      }
      setActionLoading(true);
      try {
        const formData = new FormData();
        formData.append('amount', deductAmount);
        formData.append('reason', deductReason.trim());
        
        const response = await axios.post(`${API}/admin/b2b/agents/${selectedAgent.id}/deduct-balance`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Deducted ${parseFloat(deductAmount).toFixed(2)} AED from ${selectedAgent.company_name}. New balance: ${response.data.new_balance.toFixed(2)} AED`);
        setDeductBalanceOpen(false);
        setDeductAmount('');
        setDeductReason('');
        fetchB2BAgents();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to deduct balance');
      } finally {
        setActionLoading(false);
      }
    };

    const handleUpdateCreditLimit = async () => {
      if (creditLimitAmount === '' || isNaN(parseFloat(creditLimitAmount)) || parseFloat(creditLimitAmount) < 0) {
        toast.error('Please enter a valid credit limit (0 or positive number)');
        return;
      }
      setActionLoading(true);
      try {
        const formData = new FormData();
        formData.append('credit_limit', creditLimitAmount);
        
        await axios.put(`${API}/admin/b2b/agents/${selectedAgent.id}/credit-limit`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Credit limit updated for ${selectedAgent.company_name}`);
        setCreditLimitOpen(false);
        setCreditLimitAmount('');
        fetchB2BAgents();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update credit limit');
      } finally {
        setActionLoading(false);
      }
    };

    const handleToggleAgentStatus = async (agent) => {
      const newStatus = agent.status === 'blocked' ? 'active' : 'blocked';
      const confirmMessage = newStatus === 'blocked' 
        ? `Are you sure you want to block ${agent.company_name}? They will not be able to login or submit applications.`
        : `Are you sure you want to unblock ${agent.company_name}?`;
      
      if (!window.confirm(confirmMessage)) return;
      
      setActionLoading(true);
      try {
        const formData = new FormData();
        formData.append('status', newStatus);
        
        await axios.put(`${API}/admin/b2b/agents/${agent.id}/status`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Agent ${newStatus === 'blocked' ? 'blocked' : 'activated'} successfully`);
        fetchB2BAgents();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update agent status');
      } finally {
        setActionLoading(false);
      }
    };

    const handleDeleteAgent = async (agent) => {
      if (!window.confirm(`Are you sure you want to delete ${agent.company_name}? This action cannot be undone.`)) return;
      
      setActionLoading(true);
      try {
        await axios.delete(`${API}/admin/b2b/agents/${agent.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Agent deleted successfully');
        fetchB2BAgents();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete agent');
      } finally {
        setActionLoading(false);
      }
    };

    return (
      <>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-heading">B2B Agents</CardTitle>
              <CardDescription>Manage travel agent partners</CardDescription>
            </div>
            <Button onClick={() => setCreateAgentOpen(true)} className="bg-[#009FB7] hover:bg-[#008fa5]">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Agent
            </Button>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Company</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Balance</TableHead>
                    <TableHead>Credit Limit</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {b2bAgents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        No agents found
                      </TableCell>
                    </TableRow>
                  ) : (
                    b2bAgents.map((agent) => (
                      <TableRow key={agent.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">{agent.company_name}</TableCell>
                        <TableCell>{agent.contact_person}</TableCell>
                        <TableCell>{agent.email}</TableCell>
                        <TableCell className={`font-medium ${agent.wallet_balance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {(agent.wallet_balance || 0).toFixed(2)} AED
                        </TableCell>
                        <TableCell>
                          {agent.credit_limit !== null && agent.credit_limit !== undefined ? (
                            <span className="font-medium text-blue-600">{agent.credit_limit.toFixed(2)} AED</span>
                          ) : (
                            <span className="text-slate-400">Not Set</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={agent.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {agent.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {hasPermission('action_give_credit') && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedAgent(agent);
                                  setAddCreditOpen(true);
                                }}
                                title="Add Credit"
                                className="text-green-600 border-green-200 hover:bg-green-50"
                                data-testid={`add-credit-${agent.id}`}
                              >
                                <DollarSign className="w-4 h-4" />
                              </Button>
                            )}
                            {hasPermission('action_deduct_balance') && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedAgent(agent);
                                  setDeductAmount('');
                                  setDeductReason('');
                                  setDeductBalanceOpen(true);
                                }}
                                title="Deduct Balance"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                data-testid={`deduct-balance-${agent.id}`}
                              >
                                <MinusCircle className="w-4 h-4" />
                              </Button>
                            )}
                            {hasPermission('action_give_credit') && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedAgent(agent);
                                  setCreditLimitAmount(agent.credit_limit !== null && agent.credit_limit !== undefined ? agent.credit_limit.toString() : '');
                                  setCreditLimitOpen(true);
                                }}
                                title="Set Credit Limit"
                                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                data-testid={`set-credit-limit-${agent.id}`}
                              >
                                <Wallet className="w-4 h-4" />
                              </Button>
                            )}
                            {hasPermission('action_update_rates') && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedAgent(agent);
                                  setAgentRates(agent.custom_rates || { '30_days': 400, '60_days': 600, '1m_extension': 300 });
                                  setEditAgentRatesOpen(true);
                                }}
                                title="Edit Rates"
                              >
                                <Settings className="w-4 h-4" />
                              </Button>
                            )}
                            {isSuperAdmin && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleToggleAgentStatus(agent)}
                                  title={agent.status === 'blocked' ? 'Unblock Agent' : 'Block Agent'}
                                  className={agent.status === 'blocked' ? 'text-green-600 border-green-200 hover:bg-green-50' : 'text-orange-600 border-orange-200 hover:bg-orange-50'}
                                  data-testid={`toggle-status-${agent.id}`}
                                >
                                  {agent.status === 'blocked' ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteAgent(agent)}
                                  title="Delete Agent"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  data-testid={`delete-agent-${agent.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Create Agent Dialog */}
        <Dialog open={createAgentOpen} onOpenChange={setCreateAgentOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Agent</DialogTitle>
              <DialogDescription>Add a new B2B travel agent partner</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Company Name *</Label>
                <Input
                  value={newAgentData.company_name}
                  onChange={(e) => setNewAgentData({ ...newAgentData, company_name: e.target.value })}
                  placeholder="Travel Agency Name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Contact Person</Label>
                <Input
                  value={newAgentData.contact_person}
                  onChange={(e) => setNewAgentData({ ...newAgentData, contact_person: e.target.value })}
                  placeholder="John Doe"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newAgentData.email}
                  onChange={(e) => setNewAgentData({ ...newAgentData, email: e.target.value })}
                  placeholder="agent@example.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={newAgentData.phone}
                  onChange={(e) => setNewAgentData({ ...newAgentData, phone: e.target.value })}
                  placeholder="+971501234567"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={newAgentData.password}
                  onChange={(e) => setNewAgentData({ ...newAgentData, password: e.target.value })}
                  placeholder="Enter password"
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateAgentOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateAgent} disabled={actionLoading} className="bg-[#009FB7]">
                {actionLoading ? 'Creating...' : 'Create Agent'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Credit Dialog */}
        <Dialog open={addCreditOpen} onOpenChange={setAddCreditOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Credit to {selectedAgent?.company_name}</DialogTitle>
              <DialogDescription>Add funds to agent's wallet</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Amount (AED)</Label>
                <Input
                  type="number"
                  value={addCreditAmount}
                  onChange={(e) => setAddCreditAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Input
                  value={addCreditNotes}
                  onChange={(e) => setAddCreditNotes(e.target.value)}
                  placeholder="Payment reference"
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddCreditOpen(false)}>Cancel</Button>
              <Button onClick={handleAddCredit} disabled={actionLoading} className="bg-[#009FB7]">
                {actionLoading ? 'Adding...' : 'Add Credit'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Agent Rates Dialog */}
        <Dialog open={editAgentRatesOpen} onOpenChange={setEditAgentRatesOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Rates for {selectedAgent?.company_name}</DialogTitle>
              <DialogDescription>Set custom visa rates for this agent</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>30 Days Visa (AED)</Label>
                <Input
                  type="number"
                  value={agentRates['30_days']}
                  onChange={(e) => setAgentRates({ ...agentRates, '30_days': parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>60 Days Visa (AED)</Label>
                <Input
                  type="number"
                  value={agentRates['60_days']}
                  onChange={(e) => setAgentRates({ ...agentRates, '60_days': parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>1 Month Extension (AED)</Label>
                <Input
                  type="number"
                  value={agentRates['1m_extension']}
                  onChange={(e) => setAgentRates({ ...agentRates, '1m_extension': parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditAgentRatesOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveAgentRates} disabled={actionLoading} className="bg-[#009FB7]">
                {actionLoading ? 'Saving...' : 'Save Rates'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Deduct Balance Dialog */}
        <Dialog open={deductBalanceOpen} onOpenChange={setDeductBalanceOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">Deduct Balance from {selectedAgent?.company_name}</DialogTitle>
              <DialogDescription>
                This will deduct funds from the agent's wallet. A reason is required for audit purposes.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">Current Balance</p>
                <p className="text-2xl font-bold text-[#1A4D7F]">
                  {(selectedAgent?.wallet_balance || 0).toFixed(2)} AED
                </p>
              </div>
              <div>
                <Label>Amount to Deduct (AED) *</Label>
                <Input
                  type="number"
                  value={deductAmount}
                  onChange={(e) => setDeductAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="mt-1"
                  min="0.01"
                  step="0.01"
                />
              </div>
              <div>
                <Label>Reason for Deduction *</Label>
                <Textarea
                  value={deductReason}
                  onChange={(e) => setDeductReason(e.target.value)}
                  placeholder="e.g., Fine for incorrect submission, Balance adjustment, Refund reversal..."
                  className="mt-1"
                  rows={3}
                />
                <p className="text-xs text-slate-500 mt-1">This reason will be recorded in the transaction history</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeductBalanceOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleDeductBalance} 
                disabled={actionLoading || !deductAmount || !deductReason.trim()} 
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-deduct-btn"
              >
                {actionLoading ? 'Processing...' : 'Deduct Balance'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Credit Limit Dialog */}
        <Dialog open={creditLimitOpen} onOpenChange={setCreditLimitOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-blue-600">Set Credit Limit for {selectedAgent?.company_name}</DialogTitle>
              <DialogDescription>
                Credit limit controls how much negative balance an agent can have before visa downloads are blocked.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-slate-600">Current Balance</p>
                    <p className={`text-2xl font-bold ${selectedAgent?.wallet_balance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {(selectedAgent?.wallet_balance || 0).toFixed(2)} AED
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Current Credit Limit</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {selectedAgent?.credit_limit !== null && selectedAgent?.credit_limit !== undefined 
                        ? `${selectedAgent.credit_limit.toFixed(2)} AED` 
                        : 'Not Set'}
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <Label>New Credit Limit (AED)</Label>
                <Input
                  type="number"
                  value={creditLimitAmount}
                  onChange={(e) => setCreditLimitAmount(e.target.value)}
                  placeholder="Enter credit limit (e.g., 5000)"
                  className="mt-1"
                  min="0"
                  step="100"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Set to 0 to block downloads when balance is negative. Higher values allow more credit.
                </p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>How it works:</strong> If the agent's wallet balance goes negative beyond the credit limit, 
                  visa downloads will be blocked until they top up or admin increases the limit.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreditLimitOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleUpdateCreditLimit} 
                disabled={actionLoading || creditLimitAmount === '' || parseFloat(creditLimitAmount) < 0} 
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="confirm-credit-limit-btn"
              >
                {actionLoading ? 'Updating...' : 'Update Credit Limit'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  function renderB2BApplicationsTab() {
    const openAppDetails = (app) => {
      setSelectedB2BApp(app);
      setB2bAppDetailsOpen(true);
    };

    const getDocumentName = (url) => {
      if (!url) return null;
      const parts = url.split('/');
      return parts[parts.length - 1];
    };

    const handleSendBack = async () => {
      if (!sendBackNote || sendBackNote.trim().length < 5) {
        toast.error('Please provide a note explaining why the application is being sent back');
        return;
      }
      setActionLoading(true);
      try {
        const formData = new FormData();
        formData.append('note', sendBackNote.trim());
        
        await axios.post(`${API}/admin/b2b/applications/${sendBackApp.id}/send-back`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Application sent back to ${sendBackApp.agent_email}`);
        setSendBackOpen(false);
        setSendBackNote('');
        setSendBackApp(null);
        fetchB2BApplications();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to send back application');
      } finally {
        setActionLoading(false);
      }
    };

    return (
      <>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl font-heading">B2B Applications</CardTitle>
                <CardDescription>View all visa applications from B2B agents with documents</CardDescription>
              </div>
              <div className="flex gap-2">
                <Select value={b2bAppFilter} onValueChange={setB2bAppFilter}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Applications</SelectItem>
                    <SelectItem value="draft">Drafts (Saved)</SelectItem>
                    <SelectItem value="pending">Submitted - Pending</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="sent_back">Sent Back</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Agent</TableHead>
                    <TableHead>Applicant</TableHead>
                    <TableHead>Passport</TableHead>
                    <TableHead>Visa Type</TableHead>
                    <TableHead>Fee</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {b2bApplications.filter(app => 
                    b2bAppFilter === 'all' || app.status === b2bAppFilter
                  ).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                        No {b2bAppFilter !== 'all' ? b2bAppFilter : ''} B2B applications found
                      </TableCell>
                    </TableRow>
                  ) : (
                    b2bApplications.filter(app => 
                      b2bAppFilter === 'all' || app.status === b2bAppFilter
                    ).map((app) => (
                      <TableRow key={app.id} className="hover:bg-slate-50">
                        <TableCell className="text-sm">{app.agent_email}</TableCell>
                        <TableCell className="font-medium">{app.first_name} {app.last_name}</TableCell>
                        <TableCell>
                          <code className="bg-slate-100 px-2 py-1 rounded text-xs">
                            {app.passport_number}
                          </code>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {app.visa_duration === '30_days' ? '30 Days' : 
                             app.visa_duration === '60_days' ? '60 Days' : '1M Extension'}
                          </Badge>
                        </TableCell>
                        <TableCell>{app.total_visa_fee} AED</TableCell>
                        <TableCell>
                          {app.is_done_base ? (
                            <Badge className="bg-orange-100 text-orange-800 border-orange-300">
                              Done Base {!app.is_charged && '(Unpaid)'}
                            </Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-800 border-blue-300">
                              Normal
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(app.status)}>
                            {app.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{new Date(app.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openAppDetails(app)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditB2BApplication(app)}
                              className="border-blue-500 text-blue-600 hover:bg-blue-50"
                              data-testid={`edit-b2b-app-${app.id}`}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            {(app.status === 'pending' || app.status === 'processing') && (
                              <Button 
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSendBackApp(app);
                                  setSendBackNote('');
                                  setSendBackOpen(true);
                                }}
                                className="border-amber-500 text-amber-600 hover:bg-amber-50"
                                data-testid={`send-back-${app.id}`}
                              >
                                <RefreshCcw className="w-4 h-4 mr-1" />
                                Send Back
                              </Button>
                            )}
                            {app.status === 'approved' && !app.visa_document_url && (
                              <Button 
                                size="sm"
                                onClick={() => openVisaUploadModal(app)}
                                className="bg-emerald-600 hover:bg-emerald-700"
                              >
                                <Upload className="w-4 h-4 mr-1" />
                                Upload Visa
                              </Button>
                            )}
                            {app.visa_document_url && (
                              <a 
                                href={`${BACKEND_URL}${app.visa_document_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  className="border-emerald-600 text-emerald-600"
                                >
                                  <Download className="w-4 h-4 mr-1" />
                                  Visa
                                </Button>
                              </a>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Application Details Modal */}
        <Dialog open={b2bAppDetailsOpen} onOpenChange={setB2bAppDetailsOpen}>
          <DialogContent className="max-w-4xl w-[95vw]" style={{ maxHeight: '95vh', height: 'auto', overflow: 'auto' }}>
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Application Details</DialogTitle>
              <DialogDescription>
                View applicant information and download documents
              </DialogDescription>
            </DialogHeader>
            {selectedB2BApp && (
              <div className="space-y-6 pb-4" style={{ overflowY: 'auto', maxHeight: 'calc(95vh - 100px)' }}>
                {/* Applicant Info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm text-slate-500">Full Name</p>
                    <p className="font-medium">{selectedB2BApp.first_name} {selectedB2BApp.middle_name || ''} {selectedB2BApp.last_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Passport Number</p>
                    <p className="font-medium font-mono">{selectedB2BApp.passport_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Nationality</p>
                    <p className="font-medium">{selectedB2BApp.nationality}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Date of Birth</p>
                    <p className="font-medium">{selectedB2BApp.date_of_birth}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Occupation</p>
                    <p className="font-medium">{selectedB2BApp.occupation || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Visa Duration</p>
                    <p className="font-medium">
                      {selectedB2BApp.visa_duration === '30_days' ? '30 Days' : 
                       selectedB2BApp.visa_duration === '60_days' ? '60 Days' : '1M Extension'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Passport Issue Date</p>
                    <p className="font-medium">{selectedB2BApp.date_of_issue || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Passport Expiry Date</p>
                    <p className="font-medium">{selectedB2BApp.date_of_expiry || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <Badge className={getStatusColor(selectedB2BApp.status)}>{selectedB2BApp.status}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Fee</p>
                    <p className="font-medium text-[#1A4D7F]">{selectedB2BApp.total_visa_fee} AED</p>
                  </div>
                </div>

                {/* Payment Type */}
                <div className={`p-4 rounded-lg ${selectedB2BApp.is_done_base ? 'bg-orange-50' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Payment Type</p>
                      <p className="font-medium">
                        {selectedB2BApp.is_done_base ? (
                          <span className="text-orange-700">
                            Done Base - {selectedB2BApp.done_base_type === 'family' ? 'Family Application' : 
                                        selectedB2BApp.done_base_type === 'supporting_1' ? 'With 1 Supporting Person' :
                                        selectedB2BApp.done_base_type === 'supporting_2' ? 'With 2+ Supporting Persons' : 'Pay on Approval'}
                          </span>
                        ) : (
                          <span className="text-slate-700">Normal (Prepaid)</span>
                        )}
                      </p>
                    </div>
                    {selectedB2BApp.is_done_base && (
                      <Badge className={selectedB2BApp.is_charged ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {selectedB2BApp.is_charged ? 'Charged' : 'Not Charged'}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Agent Info */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">Submitted By (Agent)</p>
                  <p className="font-medium">{selectedB2BApp.agent_email}</p>
                </div>

                {/* Documents Section */}
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#009FB7]" />
                    Documents
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <a 
                      href={selectedB2BApp.passport_url ? `${BACKEND_URL}${selectedB2BApp.passport_url}` : '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'flex', visibility: 'visible', opacity: 1 }}
                      className={`flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 transition-colors ${!selectedB2BApp.passport_url ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={(e) => !selectedB2BApp.passport_url && e.preventDefault()}
                    >
                      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                        <FileImage className="w-5 h-5 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">Passport Copy</p>
                        <p className="text-xs text-slate-500">{selectedB2BApp.passport_url ? 'Click to download' : 'Not uploaded'}</p>
                      </div>
                      {selectedB2BApp.passport_url && <Download className="w-4 h-4 text-slate-400" />}
                    </a>
                    <a 
                      href={selectedB2BApp.id_front_url ? `${BACKEND_URL}${selectedB2BApp.id_front_url}` : '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'flex', visibility: 'visible', opacity: 1 }}
                      className={`flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 transition-colors ${!selectedB2BApp.id_front_url ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={(e) => !selectedB2BApp.id_front_url && e.preventDefault()}
                    >
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FileImage className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">ID Card (Front)</p>
                        <p className="text-xs text-slate-500">{selectedB2BApp.id_front_url ? 'Click to download' : 'Not uploaded'}</p>
                      </div>
                      {selectedB2BApp.id_front_url && <Download className="w-4 h-4 text-slate-400" />}
                    </a>
                    <a 
                      href={selectedB2BApp.id_back_url ? `${BACKEND_URL}${selectedB2BApp.id_back_url}` : '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'flex', visibility: 'visible', opacity: 1 }}
                      className={`flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 transition-colors ${!selectedB2BApp.id_back_url ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={(e) => !selectedB2BApp.id_back_url && e.preventDefault()}
                    >
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FileImage className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">ID Card (Back)</p>
                        <p className="text-xs text-slate-500">{selectedB2BApp.id_back_url ? 'Click to download' : 'Not uploaded'}</p>
                      </div>
                      {selectedB2BApp.id_back_url && <Download className="w-4 h-4 text-slate-400" />}
                    </a>
                    <a 
                      href={selectedB2BApp.personal_photo_url ? `${BACKEND_URL}${selectedB2BApp.personal_photo_url}` : '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'flex', visibility: 'visible', opacity: 1 }}
                      className={`flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 transition-colors ${!selectedB2BApp.personal_photo_url ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={(e) => !selectedB2BApp.personal_photo_url && e.preventDefault()}
                    >
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <FileImage className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">Personal Photo</p>
                        <p className="text-xs text-slate-500">{selectedB2BApp.personal_photo_url ? 'Click to download' : 'Not uploaded'}</p>
                      </div>
                      {selectedB2BApp.personal_photo_url && <Download className="w-4 h-4 text-slate-400" />}
                    </a>
                    <a 
                      href={selectedB2BApp.frc_mrc_url ? `${BACKEND_URL}${selectedB2BApp.frc_mrc_url}` : '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'flex', visibility: 'visible', opacity: 1 }}
                      className={`flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 transition-colors ${!selectedB2BApp.frc_mrc_url ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={(e) => !selectedB2BApp.frc_mrc_url && e.preventDefault()}
                    >
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <File className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">FRC/MRC Document</p>
                        <p className="text-xs text-slate-500">{selectedB2BApp.frc_mrc_url ? 'Click to download' : 'Not uploaded'}</p>
                      </div>
                      {selectedB2BApp.frc_mrc_url && <Download className="w-4 h-4 text-slate-400" />}
                    </a>
                    <a 
                      href={selectedB2BApp.supporting_docs_url ? `${BACKEND_URL}${selectedB2BApp.supporting_docs_url}` : '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'flex', visibility: 'visible', opacity: 1 }}
                      className={`flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 transition-colors col-span-2 ${!selectedB2BApp.supporting_docs_url ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={(e) => !selectedB2BApp.supporting_docs_url && e.preventDefault()}
                    >
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <File className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">Supporting Documents</p>
                        <p className="text-xs text-slate-500">{selectedB2BApp.supporting_docs_url ? 'Click to download' : 'Not uploaded'}</p>
                      </div>
                      {selectedB2BApp.supporting_docs_url && <Download className="w-4 h-4 text-slate-400" />}
                    </a>
                  </div>
                </div>

                {/* Visa Document Section */}
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <h4 className="font-medium mb-3 flex items-center gap-2 text-emerald-800">
                    <FileText className="w-5 h-5" />
                    Visa Document
                  </h4>
                  {selectedB2BApp.visa_document_url ? (
                    <div className="flex items-center justify-between">
                      <a 
                        href={`${BACKEND_URL}${selectedB2BApp.visa_document_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-white border border-emerald-200 rounded-lg hover:bg-emerald-50 transition-colors flex-1 mr-3"
                      >
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm text-emerald-800">Visa Document</p>
                          <p className="text-xs text-emerald-600">Click to download</p>
                        </div>
                        <Download className="w-4 h-4 text-emerald-600" />
                      </a>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openVisaUploadModal(selectedB2BApp)}
                        className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                      >
                        <Upload className="w-4 h-4 mr-1" />
                        Replace
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-3 bg-white border border-dashed border-emerald-300 rounded-lg">
                      <p className="text-sm text-slate-500">No visa document uploaded yet</p>
                      <Button
                        size="sm"
                        onClick={() => openVisaUploadModal(selectedB2BApp)}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid="upload-visa-btn-modal"
                      >
                        <Upload className="w-4 h-4 mr-1" />
                        Upload Visa
                      </Button>
                    </div>
                  )}
                </div>

                {/* Family Members */}
                {selectedB2BApp.family_members && selectedB2BApp.family_members.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Users className="w-5 h-5 text-[#009FB7]" />
                      Family Members ({selectedB2BApp.family_members.length})
                    </h4>
                    <div className="space-y-4">
                      {selectedB2BApp.family_members.map((member, idx) => (
                        <div key={idx} className="p-4 border rounded-lg bg-slate-50">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <p className="font-medium text-lg">{member.first_name} {member.last_name}</p>
                              <p className="text-sm text-slate-500">Passport: <span className="font-mono">{member.passport_number}</span></p>
                              {member.nationality && <p className="text-sm text-slate-500">Nationality: {member.nationality}</p>}
                            </div>
                            <Badge variant="outline">Family Member {idx + 1}</Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <a 
                              href={member.passport_url ? `${BACKEND_URL}${member.passport_url}` : '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center gap-2 p-2 bg-white border rounded hover:bg-blue-50 transition-colors ${!member.passport_url ? 'opacity-50 cursor-not-allowed' : ''}`}
                              onClick={(e) => !member.passport_url && e.preventDefault()}
                            >
                              <FileImage className="w-4 h-4 text-red-500" />
                              <span className="text-sm">Passport</span>
                              {member.passport_url && <Download className="w-3 h-3 ml-auto text-slate-400" />}
                            </a>
                            <a 
                              href={member.personal_photo_url ? `${BACKEND_URL}${member.personal_photo_url}` : '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center gap-2 p-2 bg-white border rounded hover:bg-blue-50 transition-colors ${!member.personal_photo_url ? 'opacity-50 cursor-not-allowed' : ''}`}
                              onClick={(e) => !member.personal_photo_url && e.preventDefault()}
                            >
                              <FileImage className="w-4 h-4 text-purple-500" />
                              <span className="text-sm">Personal Photo</span>
                              {member.personal_photo_url && <Download className="w-3 h-3 ml-auto text-slate-400" />}
                            </a>
                            <a 
                              href={member.id_front_url ? `${BACKEND_URL}${member.id_front_url}` : '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center gap-2 p-2 bg-white border rounded hover:bg-blue-50 transition-colors ${!member.id_front_url ? 'opacity-50 cursor-not-allowed' : ''}`}
                              onClick={(e) => !member.id_front_url && e.preventDefault()}
                            >
                              <FileImage className="w-4 h-4 text-blue-500" />
                              <span className="text-sm">ID Front</span>
                              {member.id_front_url && <Download className="w-3 h-3 ml-auto text-slate-400" />}
                            </a>
                            <a 
                              href={member.id_back_url ? `${BACKEND_URL}${member.id_back_url}` : '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center gap-2 p-2 bg-white border rounded hover:bg-blue-50 transition-colors ${!member.id_back_url ? 'opacity-50 cursor-not-allowed' : ''}`}
                              onClick={(e) => !member.id_back_url && e.preventDefault()}
                            >
                              <FileImage className="w-4 h-4 text-blue-500" />
                              <span className="text-sm">ID Back</span>
                              {member.id_back_url && <Download className="w-3 h-3 ml-auto text-slate-400" />}
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Supporting Persons - BASIC VERSION */}
                {selectedB2BApp.supporting_persons && selectedB2BApp.supporting_persons.length > 0 && (
                  <div className="mt-5 p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <h4 className="text-base font-bold text-orange-700 mb-2">
                      Supporting Persons ({selectedB2BApp.supporting_persons.length})
                    </h4>
                    <p className="text-xs text-orange-600 mb-4">
                      Note: Supporting persons provide documents only. Only the main applicant receives a visa.
                    </p>
                    {selectedB2BApp.supporting_persons.map((person, idx) => (
                      <div key={idx} className="mb-4 p-4 bg-white rounded-lg border border-orange-300">
                        <p className="font-bold text-sm mb-3">{person.name} <span className="font-normal text-gray-500">({person.relation})</span></p>
                        <div className="space-y-2">
                          {person.passport_url && (
                            <a href={`${BACKEND_URL}${person.passport_url}`} target="_blank" rel="noopener noreferrer" 
                               className="block px-3 py-2 rounded bg-red-100 text-red-600 text-sm hover:bg-red-200">
                              📄 Passport
                            </a>
                          )}
                          {person.personal_photo_url && (
                            <a href={`${BACKEND_URL}${person.personal_photo_url}`} target="_blank" rel="noopener noreferrer"
                               className="block px-3 py-2 rounded bg-purple-100 text-purple-600 text-sm hover:bg-purple-200">
                              📷 Personal Photo
                            </a>
                          )}
                          {person.cnic_front_url && (
                            <a href={`${BACKEND_URL}${person.cnic_front_url}`} target="_blank" rel="noopener noreferrer"
                               className="block px-3 py-2 rounded bg-blue-100 text-blue-600 text-sm hover:bg-blue-200">
                              🪪 ID Card Front
                            </a>
                          )}
                          {person.cnic_back_url && (
                            <a href={`${BACKEND_URL}${person.cnic_back_url}`} target="_blank" rel="noopener noreferrer"
                               className="block px-3 py-2 rounded bg-blue-100 text-blue-600 text-sm hover:bg-blue-200">
                              🪪 ID Card Back
                            </a>
                          )}
                          {person.frc_mrc_url && (
                            <a href={`${BACKEND_URL}${person.frc_mrc_url}`} target="_blank" rel="noopener noreferrer"
                               className="block px-3 py-2 rounded bg-green-100 text-green-600 text-sm hover:bg-green-200">
                              📜 FRC/MRC
                            </a>
                          )}
                          {!person.passport_url && !person.personal_photo_url && !person.cnic_front_url && !person.cnic_back_url && !person.frc_mrc_url && (
                            <p className="text-gray-400 text-sm">No documents uploaded</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* GDRFAD Reference */}
                {selectedB2BApp.gdrfad_reference && (
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600 mb-1">GDRFAD Reference</p>
                    <p className="font-mono font-medium">{selectedB2BApp.gdrfad_reference}</p>
                  </div>
                )}

                {/* Sent Back Info */}
                {selectedB2BApp.status === 'sent_back' && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-600 mb-1">Sent Back to Agent</p>
                    <p className="font-medium text-amber-800">{selectedB2BApp.admin_notes}</p>
                    {selectedB2BApp.sent_back_by && (
                      <p className="text-xs text-amber-600 mt-2">
                        By: {selectedB2BApp.sent_back_by} on {new Date(selectedB2BApp.sent_back_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setB2bAppDetailsOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Send Back Application Dialog */}
        <Dialog open={sendBackOpen} onOpenChange={setSendBackOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-amber-600">Send Application Back to Agent</DialogTitle>
              <DialogDescription>
                This will change the status to "sent_back" and notify the agent to correct the application.
              </DialogDescription>
            </DialogHeader>
            {sendBackApp && (
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">Application</p>
                  <p className="font-medium">{sendBackApp.first_name} {sendBackApp.last_name}</p>
                  <p className="text-sm text-slate-500">Passport: {sendBackApp.passport_number}</p>
                  <p className="text-sm text-slate-500">Agent: {sendBackApp.agent_email}</p>
                </div>
                <div>
                  <Label>Note to Agent *</Label>
                  <Textarea
                    value={sendBackNote}
                    onChange={(e) => setSendBackNote(e.target.value)}
                    placeholder="Explain what needs to be corrected (e.g., unclear passport scan, missing document, incorrect information)..."
                    className="mt-1"
                    rows={4}
                  />
                  <p className="text-xs text-slate-500 mt-1">This note will be visible to the agent</p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setSendBackOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleSendBack} 
                disabled={actionLoading || !sendBackNote || sendBackNote.trim().length < 5} 
                className="bg-amber-500 hover:bg-amber-600"
                data-testid="confirm-send-back-btn"
              >
                {actionLoading ? 'Sending...' : 'Send Back'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Admin Edit B2B Application Dialog */}
        <Dialog open={editB2BAppOpen} onOpenChange={setEditB2BAppOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit B2B Application (Admin)</DialogTitle>
              <DialogDescription>
                Modify application details and pricing. Changes to pricing will adjust agent wallet accordingly.
              </DialogDescription>
            </DialogHeader>
            {editingB2BApp && (
              <div className="space-y-4">
                {/* Custom Pricing Section - Highlighted */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <Label className="text-sm font-medium text-amber-800 flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4" />
                    Custom Pricing (Per Person)
                  </Label>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      step="0.01"
                      value={editingB2BApp.custom_visa_fee_per_person}
                      onChange={(e) => setEditingB2BApp({...editingB2BApp, custom_visa_fee_per_person: parseFloat(e.target.value) || 0})}
                      className="w-40"
                    />
                    <span className="text-sm text-slate-600">AED per person</span>
                    <span className="text-sm text-slate-500">×</span>
                    <span className="text-sm font-medium">{editingB2BApp.total_applicants || 1} applicants</span>
                    <span className="text-sm text-slate-500">=</span>
                    <span className="text-lg font-bold text-amber-700">
                      {((editingB2BApp.custom_visa_fee_per_person || 0) * (editingB2BApp.total_applicants || 1)).toFixed(2)} AED
                    </span>
                  </div>
                  {editingB2BApp.is_charged && (
                    <p className="text-xs text-amber-600 mt-2">
                      <AlertCircle className="w-3 h-3 inline mr-1" />
                      Changing price will adjust agent's wallet (debit if increased, credit if decreased)
                    </p>
                  )}
                  {editingB2BApp.custom_rate_applied && (
                    <p className="text-xs text-blue-600 mt-1">
                      Original rate: {editingB2BApp.original_fee_per_person || 'N/A'} AED/person
                    </p>
                  )}
                </div>

                {/* Personal Info */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">First Name *</Label>
                    <Input
                      value={editingB2BApp.first_name}
                      onChange={(e) => setEditingB2BApp({...editingB2BApp, first_name: e.target.value.toUpperCase()})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Middle Name</Label>
                    <Input
                      value={editingB2BApp.middle_name}
                      onChange={(e) => setEditingB2BApp({...editingB2BApp, middle_name: e.target.value.toUpperCase()})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Last Name *</Label>
                    <Input
                      value={editingB2BApp.last_name}
                      onChange={(e) => setEditingB2BApp({...editingB2BApp, last_name: e.target.value.toUpperCase()})}
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Passport Info */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Passport Number *</Label>
                    <Input
                      value={editingB2BApp.passport_number}
                      onChange={(e) => setEditingB2BApp({...editingB2BApp, passport_number: e.target.value.toUpperCase()})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Date of Issue *</Label>
                    <Input
                      type="date"
                      value={editingB2BApp.date_of_issue}
                      onChange={(e) => setEditingB2BApp({...editingB2BApp, date_of_issue: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Date of Expiry *</Label>
                    <Input
                      type="date"
                      value={editingB2BApp.date_of_expiry}
                      onChange={(e) => setEditingB2BApp({...editingB2BApp, date_of_expiry: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Other Info */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Date of Birth *</Label>
                    <Input
                      type="date"
                      value={editingB2BApp.date_of_birth}
                      onChange={(e) => setEditingB2BApp({...editingB2BApp, date_of_birth: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Nationality *</Label>
                    <Input
                      value={editingB2BApp.nationality}
                      onChange={(e) => setEditingB2BApp({...editingB2BApp, nationality: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Occupation *</Label>
                    <Input
                      value={editingB2BApp.occupation}
                      onChange={(e) => setEditingB2BApp({...editingB2BApp, occupation: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Additional Info */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Father Name</Label>
                    <Input
                      value={editingB2BApp.father_name}
                      onChange={(e) => setEditingB2BApp({...editingB2BApp, father_name: e.target.value.toUpperCase()})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Mother Name</Label>
                    <Input
                      value={editingB2BApp.mother_name}
                      onChange={(e) => setEditingB2BApp({...editingB2BApp, mother_name: e.target.value.toUpperCase()})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Mobile</Label>
                    <Input
                      value={editingB2BApp.mobile}
                      onChange={(e) => setEditingB2BApp({...editingB2BApp, mobile: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Address</Label>
                    <Input
                      value={editingB2BApp.address}
                      onChange={(e) => setEditingB2BApp({...editingB2BApp, address: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Admin Notes</Label>
                    <Input
                      value={editingB2BApp.admin_notes}
                      onChange={(e) => setEditingB2BApp({...editingB2BApp, admin_notes: e.target.value})}
                      className="mt-1"
                      placeholder="Internal notes (visible to agent)"
                    />
                  </div>
                </div>

                {/* Document Updates */}
                <div className="border-t pt-4">
                  <Label className="text-sm font-medium">Replace Documents (Optional)</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {[
                      { key: 'new_passport_doc', label: 'Passport', current: editingB2BApp.passport_doc_url },
                      { key: 'new_personal_photo_doc', label: 'Personal Photo', current: editingB2BApp.personal_photo_url },
                      { key: 'new_id_front_doc', label: 'ID Front', current: editingB2BApp.id_front_url },
                      { key: 'new_id_back_doc', label: 'ID Back', current: editingB2BApp.id_back_url },
                    ].map(doc => (
                      <div key={doc.key} className="space-y-1">
                        <Label className="text-xs text-slate-500">
                          {doc.label} {doc.current && <span className="text-green-600">(has file)</span>}
                        </Label>
                        <Input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => setEditingB2BApp(prev => ({
                            ...prev,
                            [doc.key]: e.target.files[0]
                          }))}
                          className="text-xs"
                        />
                        {editingB2BApp[doc.key] && (
                          <p className="text-xs text-green-600">{editingB2BApp[doc.key].name}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Application Info */}
                <div className="p-3 bg-slate-50 rounded-lg text-xs text-slate-600">
                  <div className="flex justify-between">
                    <span>Status: <strong>{editingB2BApp.status}</strong></span>
                    <span>Agent: <strong>{editingB2BApp.agent_email}</strong></span>
                    <span>Charged: <strong>{editingB2BApp.is_charged ? 'Yes' : 'No'}</strong></span>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditB2BAppOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleSaveB2BAppEdit}
                disabled={editB2BAppLoading}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="save-admin-edit-btn"
              >
                {editB2BAppLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  function renderCreditRequestsTab() {
    const handleApproveCredit = async (requestId) => {
      try {
        await axios.put(`${API}/admin/b2b/credit-requests/${requestId}/approve`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Credit request approved');
        fetchCreditRequests();
        fetchB2BAgents();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to approve');
      }
    };

    const handleRejectCredit = async (requestId) => {
      try {
        await axios.put(`${API}/admin/b2b/credit-requests/${requestId}/reject`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Credit request rejected');
        fetchCreditRequests();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to reject');
      }
    };

    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-heading">Credit Requests</CardTitle>
          <CardDescription>Review and approve agent credit requests</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Agent</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Receipt</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {creditRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      No credit requests found
                    </TableCell>
                  </TableRow>
                ) : (
                  creditRequests.map((req) => (
                    <TableRow key={req.id} className="hover:bg-slate-50">
                      <TableCell>{req.agent_email}</TableCell>
                      <TableCell className="font-medium">{req.amount.toFixed(2)} AED</TableCell>
                      <TableCell className="max-w-[200px] truncate">{req.notes || '-'}</TableCell>
                      <TableCell>
                        {req.receipt_url && (
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => {
                              setReceiptImageUrl(`${BACKEND_URL}${req.receipt_url}`);
                              setReceiptModalOpen(true);
                            }}
                            data-testid={`view-receipt-${req.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View Receipt
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          req.status === 'approved' ? 'bg-green-100 text-green-800' :
                          req.status === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }>
                          {req.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{new Date(req.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        {req.status === 'pending' && hasPermission('action_approve_credit_requests') && (
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleApproveCredit(req.id)}
                              className="bg-green-600 hover:bg-green-700 h-7 px-2"
                              data-testid={`approve-credit-${req.id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRejectCredit(req.id)}
                              className="text-red-600 border-red-200 hover:bg-red-50 h-7 px-2"
                              data-testid={`reject-credit-${req.id}`}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    );
  }


  function renderB2BRatesTab() {
    // This tab manages agent-specific rates
    // Admin selects an agent, then manages rates for that agent
    
    const handleSelectAgent = (agent) => {
      setSelectedAgentForRates(agent);
      fetchAgentRates(agent.id);
    };

    const handleAddAgentRate = async () => {
      if (!newAgentRate.nationality || !newAgentRate.rate_30_days || !newAgentRate.rate_60_days || !newAgentRate.rate_1m_extension) {
        toast.error('All regular rate fields are required');
        return;
      }
      setActionLoading(true);
      try {
        const formData = new FormData();
        formData.append('nationality', newAgentRate.nationality);
        formData.append('rate_30_days', newAgentRate.rate_30_days);
        formData.append('rate_60_days', newAgentRate.rate_60_days);
        formData.append('rate_1m_extension', newAgentRate.rate_1m_extension);
        formData.append('done_base_30_days', newAgentRate.done_base_30_days || '0');
        formData.append('done_base_60_days', newAgentRate.done_base_60_days || '0');
        formData.append('done_base_1m_extension', newAgentRate.done_base_1m_extension || '0');
        formData.append('supporting_1_30_days', newAgentRate.supporting_1_30_days || '0');
        formData.append('supporting_1_60_days', newAgentRate.supporting_1_60_days || '0');
        formData.append('supporting_1_1m_extension', newAgentRate.supporting_1_1m_extension || '0');
        formData.append('supporting_2_30_days', newAgentRate.supporting_2_30_days || '0');
        formData.append('supporting_2_60_days', newAgentRate.supporting_2_60_days || '0');
        formData.append('supporting_2_1m_extension', newAgentRate.supporting_2_1m_extension || '0');
        
        await axios.post(`${API}/admin/b2b/agents/${selectedAgentForRates.id}/rates`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Rate for ${newAgentRate.nationality} added to ${selectedAgentForRates.company_name}`);
        setAddAgentRateOpen(false);
        setNewAgentRate({ nationality: '', rate_30_days: '', rate_60_days: '', rate_1m_extension: '', done_base_30_days: '', done_base_60_days: '', done_base_1m_extension: '', supporting_1_30_days: '', supporting_1_60_days: '', supporting_1_1m_extension: '', supporting_2_30_days: '', supporting_2_60_days: '', supporting_2_1m_extension: '' });
        fetchAgentRates(selectedAgentForRates.id);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to add rate');
      } finally {
        setActionLoading(false);
      }
    };

    const handleUpdateAgentRate = async () => {
      if (!newAgentRate.rate_30_days || !newAgentRate.rate_60_days || !newAgentRate.rate_1m_extension) {
        toast.error('All regular rates are required');
        return;
      }
      setActionLoading(true);
      try {
        const formData = new FormData();
        formData.append('nationality', selectedAgentRate);
        formData.append('rate_30_days', newAgentRate.rate_30_days);
        formData.append('rate_60_days', newAgentRate.rate_60_days);
        formData.append('rate_1m_extension', newAgentRate.rate_1m_extension);
        formData.append('done_base_30_days', newAgentRate.done_base_30_days || '0');
        formData.append('done_base_60_days', newAgentRate.done_base_60_days || '0');
        formData.append('done_base_1m_extension', newAgentRate.done_base_1m_extension || '0');
        formData.append('supporting_1_30_days', newAgentRate.supporting_1_30_days || '0');
        formData.append('supporting_1_60_days', newAgentRate.supporting_1_60_days || '0');
        formData.append('supporting_1_1m_extension', newAgentRate.supporting_1_1m_extension || '0');
        formData.append('supporting_2_30_days', newAgentRate.supporting_2_30_days || '0');
        formData.append('supporting_2_60_days', newAgentRate.supporting_2_60_days || '0');
        formData.append('supporting_2_1m_extension', newAgentRate.supporting_2_1m_extension || '0');
        
        await axios.post(`${API}/admin/b2b/agents/${selectedAgentForRates.id}/rates`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Rate for ${selectedAgentRate} updated`);
        setEditAgentRateOpen(false);
        setSelectedAgentRate(null);
        setNewAgentRate({ nationality: '', rate_30_days: '', rate_60_days: '', rate_1m_extension: '', done_base_30_days: '', done_base_60_days: '', done_base_1m_extension: '', supporting_1_30_days: '', supporting_1_60_days: '', supporting_1_1m_extension: '', supporting_2_30_days: '', supporting_2_60_days: '', supporting_2_1m_extension: '' });
        fetchAgentRates(selectedAgentForRates.id);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update rate');
      } finally {
        setActionLoading(false);
      }
    };

    const handleDeleteAgentRate = async (nationality) => {
      if (!window.confirm(`Delete rate for ${nationality} from this agent?`)) return;
      try {
        await axios.delete(`${API}/admin/b2b/agents/${selectedAgentForRates.id}/rates/${encodeURIComponent(nationality)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Rate for ${nationality} deleted`);
        fetchAgentRates(selectedAgentForRates.id);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete rate');
      }
    };

    // Get nationalities that this agent doesn't have yet
    const existingNationalities = agentRatesData.map(r => r.nationality);
    const availableNationalities = Object.keys(nationalityRates).filter(n => !existingNationalities.includes(n));

    // Nationality Groups Handlers
    const handleCreateGroup = async () => {
      if (!newGroupData.name || !newGroupData.nationalities) {
        toast.error('Group name and nationalities are required');
        return;
      }
      setActionLoading(true);
      try {
        const formData = new FormData();
        formData.append('group_name', newGroupData.name);
        formData.append('nationalities', newGroupData.nationalities);
        
        await axios.post(`${API}/admin/b2b/nationality-groups`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Group '${newGroupData.name}' created`);
        setCreateGroupOpen(false);
        setNewGroupData({ name: '', nationalities: '' });
        fetchNationalityGroups();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to create group');
      } finally {
        setActionLoading(false);
      }
    };

    const handleUpdateGroup = async () => {
      if (!newGroupData.name || !newGroupData.nationalities) {
        toast.error('Group name and nationalities are required');
        return;
      }
      setActionLoading(true);
      try {
        const formData = new FormData();
        formData.append('group_name', newGroupData.name);
        formData.append('nationalities', newGroupData.nationalities);
        
        await axios.put(`${API}/admin/b2b/nationality-groups/${selectedGroup.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Group updated`);
        setEditGroupOpen(false);
        setSelectedGroup(null);
        setNewGroupData({ name: '', nationalities: '' });
        fetchNationalityGroups();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update group');
      } finally {
        setActionLoading(false);
      }
    };

    const handleDeleteGroup = async (groupId) => {
      if (!window.confirm('Delete this nationality group?')) return;
      try {
        await axios.delete(`${API}/admin/b2b/nationality-groups/${groupId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Group deleted');
        fetchNationalityGroups();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete group');
      }
    };

    const handleApplyGroupRates = async () => {
      if (!groupRatesData.group_id || !groupRatesData.rate_30_days || !groupRatesData.rate_60_days || !groupRatesData.rate_1m_extension) {
        toast.error('Select a group and enter all required rates');
        return;
      }
      setActionLoading(true);
      try {
        const formData = new FormData();
        Object.entries(groupRatesData).forEach(([key, value]) => {
          formData.append(key, value || '0');
        });
        
        await axios.post(`${API}/admin/b2b/agents/${selectedAgentForRates.id}/rates-by-group`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Rates applied to all nationalities in group');
        setApplyGroupRatesOpen(false);
        setGroupRatesData({
          group_id: '', rate_30_days: '', rate_60_days: '', rate_1m_extension: '',
          done_base_30_days: '', done_base_60_days: '', done_base_1m_extension: '',
          supporting_1_30_days: '', supporting_1_60_days: '', supporting_1_1m_extension: '',
          supporting_2_30_days: '', supporting_2_60_days: '', supporting_2_1m_extension: ''
        });
        fetchAgentRates(selectedAgentForRates.id);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to apply rates');
      } finally {
        setActionLoading(false);
      }
    };

    return (
      <>
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-heading">Agent-Specific Rates</CardTitle>
            <CardDescription>Each agent can have different rates per nationality. Select an agent to manage their rates.</CardDescription>
          </CardHeader>
          <CardContent>
            {!selectedAgentForRates ? (
              // Agent Selection
              <div>
                <p className="text-sm text-slate-600 mb-4">Select an agent to manage their rates:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {b2bAgents.map(agent => (
                    <Card 
                      key={agent.id} 
                      className="cursor-pointer hover:border-[#009FB7] transition-colors"
                      onClick={() => handleSelectAgent(agent)}
                    >
                      <CardContent className="p-4">
                        <p className="font-medium">{agent.company_name}</p>
                        <p className="text-sm text-slate-500">{agent.email}</p>
                        <p className="text-xs text-slate-400 mt-1">Balance: {(agent.wallet_balance || 0).toFixed(2)} AED</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                {b2bAgents.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    No agents found. Create an agent first in the B2B Agents tab.
                  </div>
                )}
              </div>
            ) : (
              // Agent Rates Management
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedAgentForRates(null)} className="mb-2">
                      ← Back to Agents
                    </Button>
                    <h3 className="text-lg font-semibold">{selectedAgentForRates.company_name}</h3>
                    <p className="text-sm text-slate-500">{selectedAgentForRates.email}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      onClick={() => setApplyGroupRatesOpen(true)}
                      className="text-purple-600 border-purple-200 hover:bg-purple-50"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Apply Group Rates
                    </Button>
                    <Button onClick={() => setAddAgentRateOpen(true)} className="bg-[#009FB7] hover:bg-[#008fa5]">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Rate
                    </Button>
                  </div>
                </div>

                {/* Nationality Groups Management Section */}
                <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-purple-800">Nationality Groups</h4>
                      <p className="text-sm text-purple-600">Create groups of nationalities to apply rates in bulk</p>
                    </div>
                    <Button 
                      size="sm" 
                      onClick={() => setCreateGroupOpen(true)}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      New Group
                    </Button>
                  </div>
                  {nationalityGroups.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {nationalityGroups.map(group => (
                        <div key={group.id} className="flex items-center gap-1 bg-white px-3 py-1 rounded-full border border-purple-200">
                          <span className="text-sm font-medium">{group.name}</span>
                          <span className="text-xs text-slate-500">({group.nationalities?.length || 0})</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 ml-1"
                            onClick={() => {
                              setSelectedGroup(group);
                              setNewGroupData({
                                name: group.name,
                                nationalities: group.nationalities?.join(', ') || ''
                              });
                              setEditGroupOpen(true);
                            }}
                          >
                            <Settings className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-red-600"
                            onClick={() => handleDeleteGroup(group.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-purple-600">No groups created yet</p>
                  )}
                </div>

                {agentRatesData.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-lg">
                    <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600 font-medium">No rates configured for this agent</p>
                    <p className="text-slate-500 text-sm mt-1">Add nationalities and rates for this agent to submit applications</p>
                  </div>
                ) : (
                  <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Nationality</TableHead>
                          <TableHead className="text-right">30 Days</TableHead>
                          <TableHead className="text-right">60 Days</TableHead>
                          <TableHead className="text-right">1M Extension</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {agentRatesData.map((rate) => (
                          <TableRow key={rate.nationality}>
                            <TableCell className="font-medium">{rate.nationality}</TableCell>
                            <TableCell className="text-right">{rate.rates?.['30_days']?.toFixed(2)} AED</TableCell>
                            <TableCell className="text-right">{rate.rates?.['60_days']?.toFixed(2)} AED</TableCell>
                            <TableCell className="text-right">{rate.rates?.['1m_extension']?.toFixed(2)} AED</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedAgentRate(rate.nationality);
                                    setNewAgentRate({
                                      nationality: rate.nationality,
                                      rate_30_days: rate.rates?.['30_days'] || '',
                                      rate_60_days: rate.rates?.['60_days'] || '',
                                      rate_1m_extension: rate.rates?.['1m_extension'] || ''
                                    });
                                    setEditAgentRateOpen(true);
                                  }}
                                >
                                  <Settings className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteAgentRate(rate.nationality)}
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* B2B Document Requirements Section */}
        <Card className="mt-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-xl font-heading">B2B Document Requirements by Nationality</CardTitle>
              <CardDescription>Configure which documents are required for B2B applications per nationality</CardDescription>
            </div>
            <Button onClick={() => setAddB2BDocReqOpen(true)} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-2" />
              Set Requirements
            </Button>
          </CardHeader>
          <CardContent>
            {b2bDocumentRequirements.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No custom B2B document requirements set.</p>
                <p className="text-sm">All nationalities use default: Passport, Personal Photo, ID Front & Back (required), FRC/MRC (optional)</p>
              </div>
            ) : (
              <div className="space-y-3">
                {b2bDocumentRequirements.map((req) => (
                  <div key={req.nationality} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                    <div>
                      <p className="font-medium">{req.nationality}</p>
                      <div className="flex gap-2 mt-1 flex-wrap">
                        {req.documents?.map((doc) => (
                          <span 
                            key={doc.id} 
                            className={`text-xs px-2 py-1 rounded ${doc.required ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500 line-through'}`}
                          >
                            {doc.name}
                          </span>
                        ))}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-700"
                      onClick={async () => {
                        if (window.confirm(`Remove custom B2B requirements for ${req.nationality}? They will use defaults.`)) {
                          try {
                            await axios.delete(`${API}/admin/b2b-document-requirements/${encodeURIComponent(req.nationality)}`, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success(`Custom B2B requirements removed for ${req.nationality}`);
                            fetchB2BDocumentRequirements();
                          } catch (error) {
                            toast.error('Failed to remove requirements');
                          }
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Rate Dialog */}
        <Dialog open={addAgentRateOpen} onOpenChange={setAddAgentRateOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Rate for {selectedAgentForRates?.company_name}</DialogTitle>
              <DialogDescription>Set visa rates for a nationality for this agent</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nationality *</Label>
                <Select 
                  value={newAgentRate.nationality} 
                  onValueChange={(v) => setNewAgentRate({ ...newAgentRate, nationality: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select nationality" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableNationalities.map(nat => (
                      <SelectItem key={nat} value={nat}>{nat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>30 Days Rate (AED) *</Label>
                <Input
                  type="number"
                  value={newAgentRate.rate_30_days}
                  onChange={(e) => setNewAgentRate({ ...newAgentRate, rate_30_days: e.target.value })}
                  placeholder="e.g., 400"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>60 Days Rate (AED) *</Label>
                <Input
                  type="number"
                  value={newAgentRate.rate_60_days}
                  onChange={(e) => setNewAgentRate({ ...newAgentRate, rate_60_days: e.target.value })}
                  placeholder="e.g., 600"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>1M Extension Rate (AED) *</Label>
                <Input
                  type="number"
                  value={newAgentRate.rate_1m_extension}
                  onChange={(e) => setNewAgentRate({ ...newAgentRate, rate_1m_extension: e.target.value })}
                  placeholder="e.g., 300"
                  className="mt-1"
                />
              </div>
              
              {/* Done Base - Family Rates Section */}
              <div className="pt-4 border-t">
                <h4 className="font-medium text-orange-700 mb-3">Done Base - Family Application Rates</h4>
                <p className="text-xs text-slate-500 mb-3">Rates when family members also apply. Leave as 0 to disable.</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">30 Days (AED)</Label>
                    <Input
                      type="number"
                      value={newAgentRate.done_base_30_days}
                      onChange={(e) => setNewAgentRate({ ...newAgentRate, done_base_30_days: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">60 Days (AED)</Label>
                    <Input
                      type="number"
                      value={newAgentRate.done_base_60_days}
                      onChange={(e) => setNewAgentRate({ ...newAgentRate, done_base_60_days: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">1M Ext (AED)</Label>
                    <Input
                      type="number"
                      value={newAgentRate.done_base_1m_extension}
                      onChange={(e) => setNewAgentRate({ ...newAgentRate, done_base_1m_extension: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
              
              {/* Done Base - With 1 Supporting Person Rates */}
              <div className="pt-4 border-t">
                <h4 className="font-medium text-blue-700 mb-3">Done Base - With 1 Supporting Person</h4>
                <p className="text-xs text-slate-500 mb-3">Rates when applicant provides docs from 1 family member (mother/father/sister/wife).</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">30 Days (AED)</Label>
                    <Input
                      type="number"
                      value={newAgentRate.supporting_1_30_days}
                      onChange={(e) => setNewAgentRate({ ...newAgentRate, supporting_1_30_days: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">60 Days (AED)</Label>
                    <Input
                      type="number"
                      value={newAgentRate.supporting_1_60_days}
                      onChange={(e) => setNewAgentRate({ ...newAgentRate, supporting_1_60_days: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">1M Ext (AED)</Label>
                    <Input
                      type="number"
                      value={newAgentRate.supporting_1_1m_extension}
                      onChange={(e) => setNewAgentRate({ ...newAgentRate, supporting_1_1m_extension: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
              
              {/* Done Base - With 2 Supporting Persons Rates */}
              <div className="pt-4 border-t">
                <h4 className="font-medium text-purple-700 mb-3">Done Base - With 2 Supporting Persons</h4>
                <p className="text-xs text-slate-500 mb-3">Rates when applicant provides docs from 2 family members (e.g., mother + father).</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">30 Days (AED)</Label>
                    <Input
                      type="number"
                      value={newAgentRate.supporting_2_30_days}
                      onChange={(e) => setNewAgentRate({ ...newAgentRate, supporting_2_30_days: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">60 Days (AED)</Label>
                    <Input
                      type="number"
                      value={newAgentRate.supporting_2_60_days}
                      onChange={(e) => setNewAgentRate({ ...newAgentRate, supporting_2_60_days: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">1M Ext (AED)</Label>
                    <Input
                      type="number"
                      value={newAgentRate.supporting_2_1m_extension}
                      onChange={(e) => setNewAgentRate({ ...newAgentRate, supporting_2_1m_extension: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddAgentRateOpen(false)}>Cancel</Button>
              <Button onClick={handleAddAgentRate} disabled={actionLoading} className="bg-[#009FB7]">
                {actionLoading ? 'Adding...' : 'Add Rate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Rate Dialog */}
        <Dialog open={editAgentRateOpen} onOpenChange={setEditAgentRateOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Rate for {selectedAgentRate}</DialogTitle>
              <DialogDescription>Update visa rates for {selectedAgentForRates?.company_name}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>30 Days Rate (AED) *</Label>
                <Input
                  type="number"
                  value={newAgentRate.rate_30_days}
                  onChange={(e) => setNewAgentRate({ ...newAgentRate, rate_30_days: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>60 Days Rate (AED) *</Label>
                <Input
                  type="number"
                  value={newAgentRate.rate_60_days}
                  onChange={(e) => setNewAgentRate({ ...newAgentRate, rate_60_days: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>1M Extension Rate (AED) *</Label>
                <Input
                  type="number"
                  value={newAgentRate.rate_1m_extension}
                  onChange={(e) => setNewAgentRate({ ...newAgentRate, rate_1m_extension: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              {/* Done Base - Family Rates Section */}
              <div className="pt-4 border-t">
                <h4 className="font-medium text-orange-700 mb-3">Done Base - Family Application Rates</h4>
                <p className="text-xs text-slate-500 mb-3">Rates when family members also apply. Leave as 0 to disable.</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">30 Days (AED)</Label>
                    <Input
                      type="number"
                      value={newAgentRate.done_base_30_days}
                      onChange={(e) => setNewAgentRate({ ...newAgentRate, done_base_30_days: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">60 Days (AED)</Label>
                    <Input
                      type="number"
                      value={newAgentRate.done_base_60_days}
                      onChange={(e) => setNewAgentRate({ ...newAgentRate, done_base_60_days: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">1M Ext (AED)</Label>
                    <Input
                      type="number"
                      value={newAgentRate.done_base_1m_extension}
                      onChange={(e) => setNewAgentRate({ ...newAgentRate, done_base_1m_extension: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
              
              {/* Done Base - With 1 Supporting Person Rates */}
              <div className="pt-4 border-t">
                <h4 className="font-medium text-blue-700 mb-3">Done Base - With 1 Supporting Person</h4>
                <p className="text-xs text-slate-500 mb-3">Rates when applicant provides docs from 1 family member.</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">30 Days (AED)</Label>
                    <Input
                      type="number"
                      value={newAgentRate.supporting_1_30_days}
                      onChange={(e) => setNewAgentRate({ ...newAgentRate, supporting_1_30_days: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">60 Days (AED)</Label>
                    <Input
                      type="number"
                      value={newAgentRate.supporting_1_60_days}
                      onChange={(e) => setNewAgentRate({ ...newAgentRate, supporting_1_60_days: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">1M Ext (AED)</Label>
                    <Input
                      type="number"
                      value={newAgentRate.supporting_1_1m_extension}
                      onChange={(e) => setNewAgentRate({ ...newAgentRate, supporting_1_1m_extension: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
              
              {/* Done Base - With 2 Supporting Persons Rates */}
              <div className="pt-4 border-t">
                <h4 className="font-medium text-purple-700 mb-3">Done Base - With 2 Supporting Persons</h4>
                <p className="text-xs text-slate-500 mb-3">Rates when applicant provides docs from 2 family members.</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">30 Days (AED)</Label>
                    <Input
                      type="number"
                      value={newAgentRate.supporting_2_30_days}
                      onChange={(e) => setNewAgentRate({ ...newAgentRate, supporting_2_30_days: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">60 Days (AED)</Label>
                    <Input
                      type="number"
                      value={newAgentRate.supporting_2_60_days}
                      onChange={(e) => setNewAgentRate({ ...newAgentRate, supporting_2_60_days: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">1M Ext (AED)</Label>
                    <Input
                      type="number"
                      value={newAgentRate.supporting_2_1m_extension}
                      onChange={(e) => setNewAgentRate({ ...newAgentRate, supporting_2_1m_extension: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditAgentRateOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdateAgentRate} disabled={actionLoading} className="bg-[#009FB7]">
                {actionLoading ? 'Updating...' : 'Update Rate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Group Dialog */}
        <Dialog open={createGroupOpen} onOpenChange={setCreateGroupOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Nationality Group</DialogTitle>
              <DialogDescription>Group nationalities together to apply rates in bulk</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Group Name *</Label>
                <Input
                  value={newGroupData.name}
                  onChange={(e) => setNewGroupData({ ...newGroupData, name: e.target.value })}
                  placeholder="e.g., Asian Countries, African Countries"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Nationalities (comma-separated) *</Label>
                <textarea
                  className="w-full mt-1 p-3 border rounded-md text-sm"
                  rows={4}
                  value={newGroupData.nationalities}
                  onChange={(e) => setNewGroupData({ ...newGroupData, nationalities: e.target.value })}
                  placeholder="Pakistan, India, Bangladesh, Nepal, Sri Lanka"
                />
                <p className="text-xs text-slate-500 mt-1">Enter each nationality separated by commas</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateGroupOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateGroup} disabled={actionLoading} className="bg-purple-600">
                {actionLoading ? 'Creating...' : 'Create Group'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Group Dialog */}
        <Dialog open={editGroupOpen} onOpenChange={setEditGroupOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Nationality Group</DialogTitle>
              <DialogDescription>Modify the group name or nationalities</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Group Name *</Label>
                <Input
                  value={newGroupData.name}
                  onChange={(e) => setNewGroupData({ ...newGroupData, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Nationalities (comma-separated) *</Label>
                <textarea
                  className="w-full mt-1 p-3 border rounded-md text-sm"
                  rows={4}
                  value={newGroupData.nationalities}
                  onChange={(e) => setNewGroupData({ ...newGroupData, nationalities: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditGroupOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdateGroup} disabled={actionLoading} className="bg-purple-600">
                {actionLoading ? 'Updating...' : 'Update Group'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Apply Group Rates Dialog */}
        <Dialog open={applyGroupRatesOpen} onOpenChange={setApplyGroupRatesOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Apply Rates by Group</DialogTitle>
              <DialogDescription>Set the same rates for all nationalities in a group</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Select Nationality Group *</Label>
                <Select 
                  value={groupRatesData.group_id} 
                  onValueChange={(v) => setGroupRatesData({ ...groupRatesData, group_id: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select a group" />
                  </SelectTrigger>
                  <SelectContent>
                    {nationalityGroups.map(group => (
                      <SelectItem key={group.id} value={group.id}>
                        {group.name} ({group.nationalities?.length || 0} nationalities)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {groupRatesData.group_id && (
                  <p className="text-xs text-slate-500 mt-1">
                    Nationalities: {nationalityGroups.find(g => g.id === groupRatesData.group_id)?.nationalities?.join(', ')}
                  </p>
                )}
              </div>

              {/* Normal Rates */}
              <div className="pt-4 border-t">
                <h4 className="font-medium text-green-700 mb-3">Normal Visa Rates</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">30 Days (AED) *</Label>
                    <Input
                      type="number"
                      value={groupRatesData.rate_30_days}
                      onChange={(e) => setGroupRatesData({ ...groupRatesData, rate_30_days: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">60 Days (AED) *</Label>
                    <Input
                      type="number"
                      value={groupRatesData.rate_60_days}
                      onChange={(e) => setGroupRatesData({ ...groupRatesData, rate_60_days: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">1M Ext (AED) *</Label>
                    <Input
                      type="number"
                      value={groupRatesData.rate_1m_extension}
                      onChange={(e) => setGroupRatesData({ ...groupRatesData, rate_1m_extension: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Done Base Rates */}
              <div className="pt-4 border-t">
                <h4 className="font-medium text-orange-700 mb-3">Done Base Rates</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">30 Days (AED)</Label>
                    <Input
                      type="number"
                      value={groupRatesData.done_base_30_days}
                      onChange={(e) => setGroupRatesData({ ...groupRatesData, done_base_30_days: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">60 Days (AED)</Label>
                    <Input
                      type="number"
                      value={groupRatesData.done_base_60_days}
                      onChange={(e) => setGroupRatesData({ ...groupRatesData, done_base_60_days: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">1M Ext (AED)</Label>
                    <Input
                      type="number"
                      value={groupRatesData.done_base_1m_extension}
                      onChange={(e) => setGroupRatesData({ ...groupRatesData, done_base_1m_extension: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setApplyGroupRatesOpen(false)}>Cancel</Button>
              <Button onClick={handleApplyGroupRates} disabled={actionLoading} className="bg-purple-600">
                {actionLoading ? 'Applying...' : 'Apply Rates to Group'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* B2B Document Requirements Dialog */}
        <Dialog open={addB2BDocReqOpen} onOpenChange={setAddB2BDocReqOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Set B2B Document Requirements</DialogTitle>
              <DialogDescription>Configure which documents are required for B2B applications by nationality</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nationalities (comma-separated) *</Label>
                <Input
                  value={b2bDocReqData.nationalities}
                  onChange={(e) => setB2BDocReqData({ ...b2bDocReqData, nationalities: e.target.value })}
                  placeholder="Pakistan, India, Bangladesh"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Enter nationalities to apply these requirements to</p>
              </div>
              <div className="pt-4 border-t">
                <Label className="mb-3 block">Required Documents for B2B Applications</Label>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={b2bDocReqData.passport_doc}
                      onChange={(e) => setB2BDocReqData({ ...b2bDocReqData, passport_doc: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-medium">Passport Photo Page</p>
                      <p className="text-xs text-slate-500">Main passport page with photo</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={b2bDocReqData.personal_photo_doc}
                      onChange={(e) => setB2BDocReqData({ ...b2bDocReqData, personal_photo_doc: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-medium">Personal Photo</p>
                      <p className="text-xs text-slate-500">Passport-size photo of applicant</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={b2bDocReqData.id_front_doc}
                      onChange={(e) => setB2BDocReqData({ ...b2bDocReqData, id_front_doc: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-medium">ID Card Front</p>
                      <p className="text-xs text-slate-500">Front side of national ID card</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={b2bDocReqData.id_back_doc}
                      onChange={(e) => setB2BDocReqData({ ...b2bDocReqData, id_back_doc: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-medium">ID Card Back</p>
                      <p className="text-xs text-slate-500">Back side of national ID card</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={b2bDocReqData.frc_mrc_doc}
                      onChange={(e) => setB2BDocReqData({ ...b2bDocReqData, frc_mrc_doc: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-medium">FRC/MRC Document</p>
                      <p className="text-xs text-slate-500">Family/Marriage Registration Certificate (optional)</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddB2BDocReqOpen(false)}>Cancel</Button>
              <Button 
                onClick={async () => {
                  if (!b2bDocReqData.nationalities) {
                    toast.error('Enter at least one nationality');
                    return;
                  }
                  setActionLoading(true);
                  try {
                    const formData = new FormData();
                    formData.append('nationalities', b2bDocReqData.nationalities);
                    formData.append('passport_doc', b2bDocReqData.passport_doc);
                    formData.append('personal_photo_doc', b2bDocReqData.personal_photo_doc);
                    formData.append('id_front_doc', b2bDocReqData.id_front_doc);
                    formData.append('id_back_doc', b2bDocReqData.id_back_doc);
                    formData.append('frc_mrc_doc', b2bDocReqData.frc_mrc_doc);
                    
                    await axios.post(`${API}/admin/b2b-document-requirements/bulk`, formData, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success('B2B document requirements set');
                    setAddB2BDocReqOpen(false);
                    setB2BDocReqData({
                      nationalities: '',
                      passport_doc: true,
                      personal_photo_doc: true,
                      id_front_doc: true,
                      id_back_doc: true,
                      frc_mrc_doc: false
                    });
                    fetchB2BDocumentRequirements();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to set requirements');
                  } finally {
                    setActionLoading(false);
                  }
                }}
                disabled={actionLoading}
                className="bg-purple-600"
              >
                {actionLoading ? 'Saving...' : 'Save Requirements'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // ===== PERMISSIONS TAB (SUPER ADMIN ONLY) =====
  function renderPermissionsTab() {
    const handleUpdatePermissions = async (adminId) => {
      try {
        setActionLoading(true);
        await axios.put(
          `${API}/admin/permissions/admins/${adminId}`,
          { permissions: editingPermissions },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Permissions updated successfully');
        setPermissionsModalOpen(false);
        fetchAdminUsers();
      } catch (error) {
        console.error('Error updating permissions:', error);
        toast.error(error.response?.data?.detail || 'Failed to update permissions');
      } finally {
        setActionLoading(false);
      }
    };

    const handleMakeSuperAdmin = async (adminId) => {
      try {
        setActionLoading(true);
        await axios.post(
          `${API}/admin/permissions/admins/${adminId}/make-super-admin`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Admin promoted to super admin');
        fetchAdminUsers();
      } catch (error) {
        console.error('Error making super admin:', error);
        toast.error(error.response?.data?.detail || 'Failed to make super admin');
      } finally {
        setActionLoading(false);
      }
    };

    const handleRevokeSuperAdmin = async (adminId) => {
      try {
        setActionLoading(true);
        await axios.post(
          `${API}/admin/permissions/admins/${adminId}/revoke-super-admin`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Super admin status revoked');
        fetchAdminUsers();
      } catch (error) {
        console.error('Error revoking super admin:', error);
        toast.error(error.response?.data?.detail || 'Failed to revoke super admin status');
      } finally {
        setActionLoading(false);
      }
    };

    const openPermissionsModal = (adminUser) => {
      setSelectedAdminForPermissions(adminUser);
      setEditingPermissions(adminUser.permissions || []);
      setPermissionsModalOpen(true);
    };

    const openAgentAssignModal = async (adminUser) => {
      setSelectedAdminForPermissions(adminUser);
      await fetchAdminAssignedAgents(adminUser.id);
      setAgentAssignModalOpen(true);
    };

    const handleAssignAgents = async () => {
      try {
        setActionLoading(true);
        await axios.put(
          `${API}/admin/permissions/admins/${selectedAdminForPermissions.id}/agents`,
          { agent_ids: assignedAgentIds },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Agents assigned successfully');
        setAgentAssignModalOpen(false);
        fetchAllAgentsForAssignment();
      } catch (error) {
        console.error('Error assigning agents:', error);
        toast.error(error.response?.data?.detail || 'Failed to assign agents');
      } finally {
        setActionLoading(false);
      }
    };

    const toggleAgentAssignment = (agentId) => {
      if (assignedAgentIds.includes(agentId)) {
        setAssignedAgentIds(assignedAgentIds.filter(id => id !== agentId));
      } else {
        setAssignedAgentIds([...assignedAgentIds, agentId]);
      }
    };

    const togglePermission = (permission) => {
      if (editingPermissions.includes(permission)) {
        setEditingPermissions(editingPermissions.filter(p => p !== permission));
      } else {
        setEditingPermissions([...editingPermissions, permission]);
      }
    };

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            Admin Permissions Management
          </CardTitle>
          <CardDescription>
            Control what each admin can access and do in the system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Admin</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {adminUsers.map((adminUser) => (
                <TableRow key={adminUser.id}>
                  <TableCell className="font-medium">{adminUser.name || adminUser.email.split('@')[0]}</TableCell>
                  <TableCell>{adminUser.email}</TableCell>
                  <TableCell>
                    {adminUser.is_super_admin ? (
                      <Badge className="bg-purple-100 text-purple-800">Super Admin</Badge>
                    ) : (
                      <Badge variant="outline">Admin</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-slate-500">
                      {adminUser.is_super_admin ? 'All Data' : `${(adminUser.permissions || []).length} permissions`}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {!adminUser.is_super_admin && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openPermissionsModal(adminUser)}
                            data-testid={`edit-permissions-${adminUser.id}`}
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            Permissions
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openAgentAssignModal(adminUser)}
                            className="text-blue-600 border-blue-300 hover:bg-blue-50"
                            data-testid={`assign-agents-${adminUser.id}`}
                          >
                            <Users className="w-4 h-4 mr-1" />
                            Assign Agents
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMakeSuperAdmin(adminUser.id)}
                            className="text-purple-600 border-purple-300 hover:bg-purple-50"
                            data-testid={`make-super-admin-${adminUser.id}`}
                          >
                            <Zap className="w-4 h-4 mr-1" />
                            Make Super
                          </Button>
                        </>
                      )}
                      {adminUser.is_super_admin && adminUser.email.toLowerCase() !== 'admin@aonevisas.com' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRevokeSuperAdmin(adminUser.id)}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          data-testid={`revoke-super-admin-${adminUser.id}`}
                        >
                          <MinusCircle className="w-4 h-4 mr-1" />
                          Revoke Super
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Permissions Edit Modal */}
          <Dialog open={permissionsModalOpen} onOpenChange={setPermissionsModalOpen}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Permissions</DialogTitle>
                <DialogDescription>
                  {selectedAdminForPermissions && `Editing permissions for ${selectedAdminForPermissions.email}`}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Tab Permissions */}
                <div>
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Tab Access
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(availablePermissions)
                      .filter(([key]) => key.startsWith('tab_'))
                      .map(([key, label]) => (
                        <label key={key} className="flex items-center gap-2 p-2 border rounded hover:bg-slate-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editingPermissions.includes(key)}
                            onChange={() => togglePermission(key)}
                            className="rounded border-slate-300"
                          />
                          <span className="text-sm">{label}</span>
                        </label>
                      ))}
                  </div>
                </div>

                {/* Action Permissions */}
                <div>
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    Action Permissions
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(availablePermissions)
                      .filter(([key]) => key.startsWith('action_'))
                      .map(([key, label]) => (
                        <label key={key} className="flex items-center gap-2 p-2 border rounded hover:bg-slate-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editingPermissions.includes(key)}
                            onChange={() => togglePermission(key)}
                            className="rounded border-slate-300"
                          />
                          <span className="text-sm">{label}</span>
                        </label>
                      ))}
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setPermissionsModalOpen(false)}>Cancel</Button>
                <Button 
                  onClick={() => handleUpdatePermissions(selectedAdminForPermissions?.id)}
                  disabled={actionLoading}
                  className="bg-[#1A4D7F]"
                >
                  {actionLoading ? 'Saving...' : 'Save Permissions'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Agent Assignment Modal */}
          <Dialog open={agentAssignModalOpen} onOpenChange={setAgentAssignModalOpen}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Assign Agents</DialogTitle>
                <DialogDescription>
                  {selectedAdminForPermissions && `Select which B2B agents ${selectedAdminForPermissions.email} can manage`}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <p className="text-sm text-slate-500">
                  This admin will only see data from assigned agents (applications, credit requests, etc.)
                </p>
                <div className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto">
                  {allAgentsForAssignment.map((agent) => (
                    <label key={agent.id} className="flex items-center gap-3 p-3 border rounded hover:bg-slate-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={assignedAgentIds.includes(agent.id)}
                        onChange={() => toggleAgentAssignment(agent.id)}
                        className="rounded border-slate-300 w-4 h-4"
                      />
                      <div className="flex-1">
                        <p className="font-medium">{agent.company_name}</p>
                        <p className="text-sm text-slate-500">{agent.email}</p>
                      </div>
                      {agent.assigned_admins?.length > 0 && (
                        <span className="text-xs text-slate-400">
                          {agent.assigned_admins.length} admin(s)
                        </span>
                      )}
                    </label>
                  ))}
                </div>
                <p className="text-sm font-medium">
                  Selected: {assignedAgentIds.length} agent(s)
                </p>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setAgentAssignModalOpen(false)}>Cancel</Button>
                <Button 
                  onClick={handleAssignAgents}
                  disabled={actionLoading}
                  className="bg-[#1A4D7F]"
                >
                  {actionLoading ? 'Saving...' : 'Save Assignments'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    );
  }

  // ===== FINANCIAL STATEMENTS TAB =====
  function renderStatementsTab() {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5" />
            Financial Statements - All B2B Agents
          </CardTitle>
          <CardDescription>
            View transaction history and balance for each B2B client
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Current Balance</TableHead>
                <TableHead>Credit Limit</TableHead>
                <TableHead>Total Credits</TableHead>
                <TableHead>Total Debits</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agentsStatements.map((agent) => (
                <TableRow key={agent.agent_id}>
                  <TableCell className="font-medium">{agent.company_name}</TableCell>
                  <TableCell>{agent.email}</TableCell>
                  <TableCell>
                    <span className={agent.current_balance < 0 ? 'text-red-600 font-bold' : 'text-green-600 font-bold'}>
                      {agent.current_balance.toFixed(2)} AED
                    </span>
                  </TableCell>
                  <TableCell>{agent.credit_limit.toFixed(2)} AED</TableCell>
                  <TableCell className="text-green-600">{agent.total_credits.toFixed(2)} AED</TableCell>
                  <TableCell className="text-red-600">{agent.total_debits.toFixed(2)} AED</TableCell>
                  <TableCell>
                    <Badge className={agent.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {agent.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchAgentStatement(agent.agent_id)}
                      data-testid={`view-statement-${agent.agent_id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View Statement
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Statement Details Modal */}
          <Dialog open={statementModalOpen} onOpenChange={setStatementModalOpen}>
            <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  Statement: {selectedAgentStatement?.company_name}
                </DialogTitle>
                <DialogDescription>
                  Transaction history for {selectedAgentStatement?.email}
                </DialogDescription>
              </DialogHeader>

              {selectedAgentStatement && (
                <div className="space-y-6">
                  {/* Agent Summary Card */}
                  <div className="grid grid-cols-4 gap-4">
                    <Card className="bg-blue-50">
                      <CardContent className="p-4">
                        <p className="text-sm text-slate-500">Current Balance</p>
                        <p className={`text-xl font-bold ${selectedAgentStatement.current_balance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {selectedAgentStatement.current_balance.toFixed(2)} AED
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-50">
                      <CardContent className="p-4">
                        <p className="text-sm text-slate-500">Credit Limit</p>
                        <p className="text-xl font-bold text-green-600">
                          {selectedAgentStatement.credit_limit.toFixed(2)} AED
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <p className="text-sm text-slate-500">Contact</p>
                        <p className="font-medium">{selectedAgentStatement.contact_person}</p>
                        <p className="text-sm text-slate-500">{selectedAgentStatement.phone}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <p className="text-sm text-slate-500">Status</p>
                        <Badge className={selectedAgentStatement.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {selectedAgentStatement.status}
                        </Badge>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Transactions Table */}
                  <div>
                    <h3 className="font-medium mb-3">Transaction History ({statementTransactions.length} transactions)</h3>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Balance After</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Receipt</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {statementTransactions.map((tx) => (
                            <TableRow key={tx.id}>
                              <TableCell className="text-sm">
                                {new Date(tx.created_at).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </TableCell>
                              <TableCell>
                                <Badge className={tx.type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                  {tx.type}
                                </Badge>
                              </TableCell>
                              <TableCell className={tx.type === 'credit' ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                                {tx.type === 'credit' ? '+' : '-'}{tx.amount.toFixed(2)} AED
                              </TableCell>
                              <TableCell>{tx.balance_after?.toFixed(2)} AED</TableCell>
                              <TableCell className="max-w-xs truncate" title={tx.description}>
                                {tx.description}
                              </TableCell>
                              <TableCell>
                                {tx.receipt_url ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setReceiptImageUrl(`${BACKEND_URL}${tx.receipt_url}`);
                                      setReceiptModalOpen(true);
                                    }}
                                  >
                                    <FileImage className="w-4 h-4 mr-1" />
                                    View
                                  </Button>
                                ) : (
                                  <span className="text-slate-400 text-sm">-</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setStatementModalOpen(false)}>Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    );
  }

  function renderDialogs() {
    return (
      <>
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading">Application Details</DialogTitle>
            <DialogDescription>Complete information about this application</DialogDescription>
          </DialogHeader>

          {selectedApp && (
            <div className="space-y-6">
              {/* Visa Type Badge */}
              <div className="flex items-center gap-3">
                <Badge className="bg-blue-100 text-blue-800 px-3 py-1">
                  {selectedApp.visa_duration === '60_days' ? '60 Days Visa' : '30 Days Visa'}
                </Badge>
                <Badge variant="outline">
                  {selectedApp.total_applicants || 1} Applicant(s)
                </Badge>
                <Badge variant="outline" className="bg-green-50 text-green-700">
                  {selectedApp.total_visa_fee || 'N/A'} AED Total
                </Badge>
              </div>

              {/* Main Applicant Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-heading font-semibold text-lg text-[#1A4D7F] mb-3 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Main Applicant
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Full Name</p>
                    <p className="font-medium">{selectedApp.first_name} {selectedApp.last_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Father's Name</p>
                    <p className="font-medium">{selectedApp.father_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium">{selectedApp.user_email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Phone</p>
                    <p className="font-medium">{selectedApp.user_phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Nationality</p>
                    <p className="font-medium">{selectedApp.nationality || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Passport Number</p>
                    <p className="font-medium">{selectedApp.passport_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Place of Birth</p>
                    <p className="font-medium">{selectedApp.place_of_birth}</p>
                  </div>
                </div>

                <div className="border-t border-blue-200 mt-4 pt-4">
                  <h4 className="font-semibold mb-2 text-sm">Main Applicant Documents</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedApp.passport_photo_url && (
                      <Button
                        variant="link"
                        className="text-sm text-[#009FB7] hover:underline justify-start p-0 h-auto"
                        onClick={() => {
                          const url = `${BACKEND_URL}${selectedApp.passport_photo_url}`;
                          console.log('Opening document:', url);
                          window.open(url, '_blank');
                        }}
                      >
                        View Passport Photo
                      </Button>
                    )}
                    {selectedApp.personal_photo_url && (
                      <Button
                        variant="link"
                        className="text-sm text-[#009FB7] hover:underline justify-start p-0 h-auto"
                        onClick={() => {
                          const url = `${BACKEND_URL}${selectedApp.personal_photo_url}`;
                          console.log('Opening document:', url);
                          window.open(url, '_blank');
                        }}
                      >
                        View Personal Photo
                      </Button>
                    )}
                    {selectedApp.cnic_front_url && (
                      <Button
                        variant="link"
                        className="text-sm text-[#009FB7] hover:underline justify-start p-0 h-auto"
                        onClick={() => {
                          const url = `${BACKEND_URL}${selectedApp.cnic_front_url}`;
                          console.log('Opening document:', url);
                          window.open(url, '_blank');
                        }}
                      >
                        View CNIC Front
                      </Button>
                    )}
                    {selectedApp.cnic_back_url && (
                      <Button
                        variant="link"
                        className="text-sm text-[#009FB7] hover:underline justify-start p-0 h-auto"
                        onClick={() => {
                          const url = `${BACKEND_URL}${selectedApp.cnic_back_url}`;
                          console.log('Opening document:', url);
                          window.open(url, '_blank');
                        }}
                      >
                        View CNIC Back
                      </Button>
                    )}
                  </div>
                </div>

                {/* Main Applicant Visa Upload/Download */}
                <div className="border-t border-blue-200 mt-4 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-sm">Main Applicant Visa</h4>
                    <Button
                      size="sm"
                      onClick={() => openVisaUpload(selectedApp, 'main')}
                      className="bg-[#009FB7] hover:bg-[#008fa5]"
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      Upload Visa
                    </Button>
                  </div>
                  {selectedApp.visa_document_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`${BACKEND_URL}${selectedApp.visa_document_url}`, '_blank')}
                      className="w-full mt-2"
                    >
                      Download Approved Visa
                    </Button>
                  )}
                </div>
              </div>

              {/* Family Members Section */}
              {selectedApp.family_members && selectedApp.family_members.length > 0 && (
                <div>
                  <h3 className="font-heading font-semibold text-lg text-[#1A4D7F] mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Family Members ({selectedApp.family_members.length})
                  </h3>
                  
                  {selectedApp.family_members.map((member, index) => (
                    <div key={index} className="mb-4 bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <h4 className="font-semibold mb-3 text-slate-700">
                        Family Member {index + 1}: {member.first_name} {member.last_name}
                      </h4>
                      
                      <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                        <div>
                          <p className="text-slate-500">Father's Name</p>
                          <p className="font-medium">{member.father_name}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Passport Number</p>
                          <p className="font-medium">{member.passport_number}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-slate-500">Place of Birth</p>
                          <p className="font-medium">{member.place_of_birth}</p>
                        </div>
                      </div>

                      <div className="border-t border-slate-300 pt-3">
                        <h5 className="font-medium mb-2 text-xs text-slate-600">Documents</h5>
                        <div className="grid grid-cols-2 gap-2">
                          {member.passport_photo_url && (
                            <Button
                              variant="link"
                              className="text-xs text-[#009FB7] hover:underline justify-start p-0 h-auto"
                              onClick={() => window.open(`${BACKEND_URL}${member.passport_photo_url}`, '_blank')}
                            >
                              Passport Photo
                            </Button>
                          )}
                          {member.personal_photo_url && (
                            <Button
                              variant="link"
                              className="text-xs text-[#009FB7] hover:underline justify-start p-0 h-auto"
                              onClick={() => window.open(`${BACKEND_URL}${member.personal_photo_url}`, '_blank')}
                            >
                              Personal Photo
                            </Button>
                          )}
                          {member.cnic_front_url && (
                            <Button
                              variant="link"
                              className="text-xs text-[#009FB7] hover:underline justify-start p-0 h-auto"
                              onClick={() => window.open(`${BACKEND_URL}${member.cnic_front_url}`, '_blank')}
                            >
                              CNIC Front
                            </Button>
                          )}
                          {member.cnic_back_url && (
                            <Button
                              variant="link"
                              className="text-xs text-[#009FB7] hover:underline justify-start p-0 h-auto"
                              onClick={() => window.open(`${BACKEND_URL}${member.cnic_back_url}`, '_blank')}
                            >
                              CNIC Back
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Family Member Visa Upload/Download */}
                      <div className="border-t border-slate-300 mt-3 pt-3">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium text-xs text-slate-600">Visa Document</h5>
                          <Button
                            size="sm"
                            onClick={() => openVisaUpload(selectedApp, 'family', index)}
                            className="bg-[#009FB7] hover:bg-[#008fa5] text-xs h-7 px-2"
                          >
                            <Upload className="w-3 h-3 mr-1" />
                            Upload
                          </Button>
                        </div>
                        {member.visa_document_url && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`${BACKEND_URL}${member.visa_document_url}`, '_blank')}
                            className="w-full text-xs h-7"
                          >
                            Download Approved Visa
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pricing Info */}
              {selectedApp.total_applicants && (
                <div className="bg-gradient-to-r from-[#1A4D7F] to-[#009FB7] rounded-lg p-4 text-white">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm opacity-90">Fee Per Person</p>
                      <p className="text-xl font-bold">{selectedApp.visa_fee_per_person || 500} AED</p>
                    </div>
                    <div>
                      <p className="text-sm opacity-90">Total Applicants</p>
                      <p className="text-xl font-bold">{selectedApp.total_applicants}</p>
                    </div>
                    <div>
                      <p className="text-sm opacity-90">Total Fee</p>
                      <p className="text-xl font-bold">{selectedApp.total_visa_fee || 500} AED</p>
                    </div>
                  </div>
                </div>
              )}

              {selectedApp.admin_notes && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Admin Notes</h4>
                  <p className="text-sm text-slate-600">{selectedApp.admin_notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={statusUpdateOpen} onOpenChange={setStatusUpdateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Application Status</DialogTitle>
            <DialogDescription>Change the status and add notes</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="mt-2" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Admin Notes</Label>
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add notes for the applicant..."
                className="mt-2"
                rows={4}
                data-testid="admin-notes-textarea"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusUpdateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleStatusUpdate}
              disabled={actionLoading}
              className="bg-[#1A4D7F] hover:bg-[#153e66]"
              data-testid="save-status-button"
            >
              {actionLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Visa Dialog */}
      <Dialog open={uploadVisaOpen} onOpenChange={setUploadVisaOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Upload Approved Visa - {uploadTarget.type === 'main' 
                ? `${selectedApp?.first_name} ${selectedApp?.last_name}` 
                : `Family Member ${uploadTarget.index + 1}`}
            </DialogTitle>
            <DialogDescription>Upload the visa document for this applicant</DialogDescription>
          </DialogHeader>

          <div>
            <Label>Visa Document</Label>
            <Input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setVisaFile(e.target.files[0])}
              className="mt-2"
              data-testid="visa-file-input"
            />
            {visaFile && (
              <p className="text-sm text-green-600 mt-2">{visaFile.name}</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadVisaOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleVisaUpload}
              disabled={actionLoading || !visaFile}
              className="bg-[#009FB7] hover:bg-[#008fa5]"
              data-testid="upload-visa-button"
            >
              {actionLoading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Admin Dialog */}
      <Dialog open={createAdminOpen} onOpenChange={setCreateAdminOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Admin</DialogTitle>
            <DialogDescription>Add a new administrator account</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newAdminData.email}
                onChange={(e) => setNewAdminData({ ...newAdminData, email: e.target.value })}
                placeholder="admin@example.com"
                className="mt-2"
              />
            </div>
            <div>
              <Label>Name (optional)</Label>
              <Input
                value={newAdminData.name}
                onChange={(e) => setNewAdminData({ ...newAdminData, name: e.target.value })}
                placeholder="John Doe"
                className="mt-2"
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={newAdminData.password}
                onChange={(e) => setNewAdminData({ ...newAdminData, password: e.target.value })}
                placeholder="Enter password"
                className="mt-2"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateAdminOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateAdmin}
              disabled={actionLoading}
              className="bg-[#009FB7] hover:bg-[#008fa5]"
            >
              {actionLoading ? 'Creating...' : 'Create Admin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Set a new password for this admin</DialogDescription>
          </DialogHeader>

          <div>
            <Label>New Password</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              className="mt-2"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setChangePasswordOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleChangePassword}
              disabled={actionLoading}
              className="bg-[#009FB7] hover:bg-[#008fa5]"
            >
              {actionLoading ? 'Saving...' : 'Change Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Nationality Dialog */}
      <Dialog open={addNationalityOpen} onOpenChange={setAddNationalityOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Nationality</DialogTitle>
            <DialogDescription>Add a new nationality with visa rates for 30 and 60 days</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Nationality</Label>
              <Input
                value={newNationality.nationality}
                onChange={(e) => setNewNationality({ ...newNationality, nationality: e.target.value })}
                placeholder="e.g. Canada"
                className="mt-2"
              />
            </div>
            <div>
              <Label>30 Days Visa Rate (AED)</Label>
              <Input
                type="number"
                value={newNationality.rate_30_days}
                onChange={(e) => setNewNationality({ ...newNationality, rate_30_days: e.target.value })}
                placeholder="e.g. 500"
                className="mt-2"
              />
            </div>
            <div>
              <Label>60 Days Visa Rate (AED)</Label>
              <Input
                type="number"
                value={newNationality.rate_60_days}
                onChange={(e) => setNewNationality({ ...newNationality, rate_60_days: e.target.value })}
                placeholder="e.g. 750"
                className="mt-2"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddNationalityOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddNationality}
              disabled={actionLoading}
              className="bg-[#009FB7] hover:bg-[#008fa5]"
            >
              {actionLoading ? 'Adding...' : 'Add Nationality'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Image Modal */}
      <Dialog open={receiptModalOpen} onOpenChange={setReceiptModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Receipt Image</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center p-4">
            {receiptImageUrl && (
              <img 
                src={receiptImageUrl} 
                alt="Receipt" 
                className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="100"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%23999">Image not available</text></svg>';
                }}
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiptModalOpen(false)}>
              Close
            </Button>
            <Button 
              onClick={() => window.open(receiptImageUrl, '_blank')}
              className="bg-[#009FB7] hover:bg-[#008fa5]"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Visa Upload Modal */}
      <Dialog open={visaUploadModalOpen} onOpenChange={setVisaUploadModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Visa Document</DialogTitle>
            <DialogDescription>
              Upload visa PDF for {visaUploadApp?.first_name} {visaUploadApp?.last_name} ({visaUploadApp?.passport_number})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Visa Document (PDF)</Label>
              <Input
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => setVisaFile(e.target.files[0])}
                className="mt-2"
                data-testid="visa-file-input"
              />
            </div>
            {visaFile && (
              <p className="text-sm text-slate-500">
                Selected: {visaFile.name} ({(visaFile.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setVisaUploadModalOpen(false);
              setVisaFile(null);
              setVisaUploadApp(null);
            }}>
              Cancel
            </Button>
            <Button
              onClick={handleUploadVisa}
              disabled={visaUploading || !visaFile}
              className="bg-[#009FB7] hover:bg-[#008fa5]"
              data-testid="upload-visa-btn"
            >
              {visaUploading ? 'Uploading...' : 'Upload Visa'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
    );
  }
};

export default AdminDashboard;
